// Help.jsx — comprehensive in-app user guide.
// Each section declares its required permission via `requires`. The
// sidebar only renders sections whose permission the current user has.
// Welcome / Getting Started has no requirement and is always shown.

import React, { useMemo, useState } from 'react';
import { usePermissions } from '../context/PermissionsContext';
import { NEW_SECTIONS, REPLACE_SECTIONS, EXTRA_CONTENT, RELEASE_NOTES } from './HelpGuides';

// ---------- shared styling helpers (kept inline so this file is self-contained) ----------
const sectionWrap = { padding: '1.5rem 2rem', maxWidth: 920 };
const h2 = { color: '#A4161A', borderBottom: '2px solid #f4cdd0', paddingBottom: 8, marginTop: 0 };
const h3 = { color: '#333', marginTop: '1.6rem', marginBottom: '0.4rem' };
const para = { color: '#444', lineHeight: 1.55, fontSize: '0.95rem', margin: '0 0 0.8rem 0' };
const ul = { color: '#444', lineHeight: 1.6, fontSize: '0.95rem', paddingLeft: '1.4rem', margin: '0 0 0.8rem 0' };
const ol = { ...ul, listStyleType: 'decimal' };
const tipBox = { background: '#fff8e1', border: '1px solid #ffe082', borderLeft: '4px solid #f9a825', padding: '0.7rem 1rem', borderRadius: 6, margin: '0.8rem 0' };
const warnBox = { background: '#ffebee', border: '1px solid #ef9a9a', borderLeft: '4px solid #c62828', padding: '0.7rem 1rem', borderRadius: 6, margin: '0.8rem 0' };
const noteBox = { background: '#e3f2fd', border: '1px solid #90caf9', borderLeft: '4px solid #1565c0', padding: '0.7rem 1rem', borderRadius: 6, margin: '0.8rem 0' };
const kbd = { background: '#f4f4f4', border: '1px solid #ccc', borderRadius: 3, padding: '1px 6px', fontFamily: 'monospace', fontSize: '0.85em' };
const code = { background: '#f4f4f4', borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace', fontSize: '0.88em' };

// ---------- section content ----------
// Each entry has:
//   key      – stable id used as sidebar-link key
//   label    – sidebar label
//   requires – { pageKey, sectionKey } | array of those (any-of) | null (always visible)
//   group    – sidebar group heading
//   render   – returns the JSX of that section
const SECTIONS = [
  // ============ Getting Started ============
  {
    key: 'welcome',
    label: 'Welcome',
    group: 'Getting Started',
    requires: null,
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Welcome to SwishIQ</h2>
        <p style={para}>
          SwishIQ runs the day-to-day for your association: rostering
          referees, bench officials, court officials and coaches; processing payroll; tracking
          participant attendance; and messaging staff and parents. This guide walks through every
          page you can access.
        </p>
        <p style={para}>
          SwishIQ has two parts. <strong>SwishIQ Management</strong> is this platform: rosters, game
          planning, coaching programs, messaging, payroll and reporting. <strong>SwishIQ Trials</strong> runs
          representative trial sessions: check-in, coach scoring, selections and player feedback. At
          <strong> swishiq.snakepit.com.au</strong> you choose <strong>Trials</strong> or <strong>Management</strong>;
          Management takes you to roster.snakepit.com.au.
        </p>
        <h3 style={h3}>How to read this guide</h3>
        <p style={para}>
          The sidebar on the left lists every help topic you have permission to use. If your role
          doesn't include a feature, that topic is hidden — you can't get help for something you
          can't do. Ask an admin to grant the permission and the topic will appear next time you
          load the page.
        </p>
        <h3 style={h3}>Where things live</h3>
        <ul style={ul}>
          <li><strong>Top navigation</strong> – the main pages: Home, Game, Coach, Court, People, Message, Reports, Payroll, Admin, Help. (Home was previously labelled "Rosters" — same page, new name.)</li>
          <li><strong>Admin tab</strong> – configuration: settings, budgets, security profiles, qualifications, SMS configuration, audit log, etc.</li>
          <li><strong>My Portal</strong> – a separate, mobile-friendly site that staff and participants reach via a personal availability link (sent by email or SMS). They never need to sign in to the main app.</li>
        </ul>
        <h3 style={h3}>If something isn't working</h3>
        <p style={para}>
          Check the audit log first (Admin → Audit) — every action is logged with who did what and
          when. Most "things broke" reports trace back to either a missing permission or a
          configuration value (an env var, a setting, a credential). For SMS specifically, Admin → SMS
          has a Send Test SMS box that confirms credentials end-to-end before any bulk send.
        </p>
        <div style={tipBox}>
          <strong>Tip:</strong> Hover any field in any form for a tooltip that explains what it does.
        </div>
      </div>
    )
  },

  {
    key: 'common-workflows',
    label: 'Common Workflows',
    group: 'Getting Started',
    requires: null,
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Common workflows</h2>
        <p style={para}>
          Step-by-step recipes for the tasks people actually do. Each one names the
          page and the buttons in order — you should be able to follow along
          without trial and error.
        </p>

        <h3 style={h3}>1. Allocate referees + bench officials for the upcoming weekend</h3>
        <ol style={ol}>
          <li>Make sure the games are imported. <strong>Admin → Games</strong> → <em>Force Import Games</em> if you've changed the upstream schedule and don't want to wait for the next 10-minute cron tick.</li>
          <li>For each day of the weekend, change the date picker (the day-of-week now appears next to it so you don't second-guess) and visually scan for empty referee / bench official slots.</li>
          <li>For bulk auto-allocation: <strong>Game</strong> → <em>AI Allocate</em> button → set the date range (start + end) → <em>Run</em>. The allocator (a deterministic rule-based scorer — see the Game help section for the full rule set) enforces hard constraints (availability, team-playing conflict, no bench+ref overlap, must hold a paying qualification, Championship = 3 people, trainees never auto-assigned) then ranks eligible people by cost, historical division, same-day clustering, prior-month fairness, and a small female→female-game nudge. Nothing is saved until you click <em>Apply</em>.</li>
          <li>Review the proposed assignments in the modal. Tick the ones you want to apply. The "Allocate" button on the bottom commits them — once committed they're live in the calendar.</li>
          <li>Send rosters: <strong>Home</strong> → pick the week → SMS or email each person their personal availability link. The link goes to <em>My Portal</em> where they confirm or decline.</li>
          <li>Watch for declines: rejected assignments highlight orange in Roster View and need a reallocation pass.</li>
        </ol>

        <h3 style={h3}>2. Mark attendance after a game day</h3>
        <ol style={ol}>
          <li><strong>Game</strong> tab → set date to the day that just finished.</li>
          <li>For each game tile: tick the small box next to each assigned person to mark them attended. Untick = no-show. <em>Default</em> assumes attended once the time has passed.</li>
          <li>When all attendance is correct: <em>Lock Day</em> at the top of the page. Locking prevents accidental edits and is a prerequisite for payroll to count those games.</li>
          <li>Repeat for any other unlocked past days. Older locked days stay locked — only an admin can unlock them via Payroll → Unlock Day.</li>
        </ol>

        <h3 style={h3}>3. Run a payroll period</h3>
        <ol style={ol}>
          <li><strong>Payroll</strong> tab → <em>Payroll Maintenance</em> → confirm pay rates per qualification level (game ref level, bench level, court official level, coach level). Save changes.</li>
          <li><em>Process Payroll</em> sub-tab → set the start + end date for the payroll period. The day-of-week label confirms you're spanning the right Monday-to-Sunday block.</li>
          <li>Click <em>Generate Payroll Run</em>. Lists every locked-day assignment in range with calculated pay. Unlocked days are shown but excluded with a warning.</li>
          <li>Export to ABA / CSV for upload to your bank. The Audit log records the run + who exported.</li>
          <li><em>Payroll Report</em> sub-tab gives historical totals per person across runs.</li>
        </ol>

        <h3 style={h3}>4. Set up a coaching program from a Basketball Connect competition</h3>
        <ol style={ol}>
          <li>One-time: <strong>Admin → BC Integration</strong> → enter email / password / TOTP seed → <em>Login &amp; save fresh token</em> → <em>Test connection</em>. Should report ~32 unique competitions for your org.</li>
          <li><strong>Admin → BC Registrations</strong> → <em>Load BC competitions</em>. Wait for the status line ("Loaded N entries. Scanned X of Y registrations.").</li>
          <li><em>Add mapping</em> → in the BC Competition dropdown, pick the comp's <strong>(all registration groups)</strong> entry — that fans the mapping out across every Reg Group of the comp in one shot.</li>
          <li>Pick a Coaching Program (or <em>+ Create new program</em> with a name like the comp).</li>
          <li>Set <em>Generate sessions from / through</em> for the term's date range. Optionally pick default coaches and a location.</li>
          <li><em>Save mapping</em>. The success message lists each Reg Group + how many sessions were generated. The 4×daily auto-sync (5am, 9am, 12pm, 3pm AEDT) then keeps participants up to date — Wed players land on Wed sessions, Fri players on Fri sessions.</li>
          <li>To run an immediate sync: <em>Sync now</em> on the mapping row. Detailed counts appear (created / updated / attendance rows / per-Reg-Group breakdown).</li>
        </ol>

        <h3 style={h3}>5. Add an off-roster participant to a single coaching session</h3>
        <ol style={ol}>
          <li><strong>Coach</strong> tab → date picker → find the session.</li>
          <li>Click <em>Select Other Participants</em> at the bottom of the tile.</li>
          <li>The picker shows only people enrolled in this Coaching Program but in a <em>different</em> Registration Group from this session — exactly the off-roster set. Tick to add. They appear on this session only and don't disturb the default Reg Group filter.</li>
          <li>Untick later (before they're marked attended) to remove.</li>
        </ol>

        <h3 style={h3}>6. Triage a server error</h3>
        <ol style={ol}>
          <li><strong>Admin → Error Log</strong> → defaults to open errors, newest first.</li>
          <li>Click a row to expand: stack trace, request method + path, request body preview, user identity who triggered it. Enough to reproduce + fix.</li>
          <li>Once fixed: click <em>Resolve…</em>, type a short note ("BC token re-saved" / "fixed in commit X"), <em>Mark resolved</em>. Row stays for history but moves out of the open queue.</li>
          <li>If you want to revisit a resolved row: switch the filter to <em>Resolved</em> or <em>All</em> and click <em>Reopen</em>.</li>
        </ol>
      </div>
    )
  },

  // ============ Pages ============
  {
    key: 'game-planner',
    label: 'Game',
    group: 'Pages',
    requires: { pageKey: 'game-planner', sectionKey: 'game-planner' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Game</h2>
        <p style={para}>
          The Game Planner is where you assign referees and bench officials to scheduled games and
          mark attendance after the games are played. It shows one day at a time as a court × time
          grid.
        </p>
        <h3 style={h3}>Picking a date</h3>
        <ul style={ul}>
          <li>Use the <kbd style={kbd}>←</kbd> / <kbd style={kbd}>→</kbd> buttons next to the date picker, or pick a date directly.</li>
          <li>Days outside the available range are disabled (you can't plan past the import window).</li>
        </ul>
        <h3 style={h3}>Assigning referees and bench officials</h3>
        <p style={para}>
          Each game tile has two stacks: <strong>Referees</strong> and <strong>Bench Officials</strong>.
          Each stack supports up to 5 people.
        </p>
        <ol style={ol}>
          <li>Open the <em>+ Assign Referee</em> (or <em>+ Assign Official</em>) dropdown on a game tile.</li>
          <li>The list shows everyone qualified for that division, ordered by cheapest pay rate first. Names tagged <em>(Play)</em> are players themselves at the same timeslot, and <em>(Excluded)</em> are excluded from one of the game's teams (Team Exclusions). Names below the <em>---Unavailable---</em> divider are blocked by their availability.</li>
          <li>Pick someone — they're saved immediately, no Submit needed.</li>
          <li>Repeat to add a second/third/up to fifth person.</li>
          <li>The small <kbd style={kbd}>×</kbd> next to an assigned name removes them.</li>
        </ol>
        <div style={noteBox}>
          A person can't be on two courts at the same time — they're filtered out of dropdowns for any conflicting timeslot on other courts.
        </div>
        <h3 style={h3}>Attendance ticks</h3>
        <p style={para}>
          The checkbox next to each assigned name marks them as attended. It only becomes editable
          after the scheduled game time passes. Marking someone attended is what triggers their
          actual pay in payroll — forecast pay is calculated from the assignment, actual pay is
          calculated from attendance.
        </p>
        <h3 style={h3}>Locking a day</h3>
        <p style={para}>
          The <strong>Lock Day</strong> button freezes that day's assignments and attendance — no
          further changes possible. Use it once payroll has run for the day. Locked days appear with
          a banner and disabled inputs. Lock Day is only visible to users with edit permission on the
          Game page.
        </p>
        <h3 style={h3}>All Attended</h3>
        <p style={para}>
          One-click bulk action: marks every referee + bench official assignment on the selected day
          as attended. Only flips slots that aren't already attended; nothing is reset. Useful when a
          full day went exactly as scheduled and you don't want to tick 60 boxes by hand.
        </p>
        <h3 style={h3}>Clear Roster</h3>
        <p style={para}>
          Destructive bulk action — the red button next to All Attended. Deletes <strong>every</strong>{' '}
          referee + bench official assignment for the selected day, including any that have been
          marked attended. Two confirmation dialogs guard against accidental clicks. Locked days are
          protected (unlock first via Payroll &rarr; Unlock Day). Use when you want to start a day's
          roster from scratch — e.g. before a fresh AI Allocate run, or to wipe a day that was
          imported from the wrong source. Cannot be undone; the deleted assignments are gone, not
          archived. Audit log captures the slot counts for each clear.
        </p>
        <h3 style={h3}>Showing the winning team</h3>
        <p style={para}>
          Once both team scores are imported, the Game Planner tile renders the matchup with the
          winner in <strong>bold</strong> and the loser in normal weight (e.g. <strong>Hornets (45)</strong> vs Bulldogs (32)).
          Tied games and games still missing a score render at the default weight. The same
          formatting applies on the Home page (by-person, by-court, by-day views) so the result is
          obvious wherever you look.
        </p>
        <h3 style={h3}>AI Allocate</h3>
        <p style={para}>
          The <strong>AI Allocate</strong> button opens a modal where you pick a date range and click
          <em>Run</em>. The button is only visible to users with edit permission. Despite the name,
          this is a <em>deterministic rule-based scorer</em> — no LLM or randomness. Re-running on the
          same data gives the same answer.
        </p>
        <h3 style={h3}>What it skips automatically</h3>
        <ul style={ul}>
          <li><strong>Locked days</strong> — existing assignments are preserved.</li>
          <li><strong>Games with confirmed attendance</strong> — anyone marked attended means the slot stays as-is.</li>
        </ul>
        <h3 style={h3}>People per game</h3>
        <ul style={ul}>
          <li><strong>Referees:</strong> 2 per game by default.</li>
          <li><strong>Bench:</strong> 1 per game by default.</li>
          <li><strong>Championship exception:</strong> any division whose name contains "Championship" (Championship Men, Championship Women, etc.) gets <strong>3 referees AND 3 bench</strong>, and <strong>at least one of those referees must hold a senior tier</strong> — Association Advanced / Veterans 1, State Development, State Grading / Veterans 2, or National Development / National. If the top-scored picks don't include a senior, the allocator swaps the weakest non-senior pick for the best available senior. If no eligible senior exists it's flagged in "Couldn't make" for manual placement.</li>
          <li><strong>Trainees are never auto-assigned.</strong> The Trainee classification (qualification level contains "Trainee") is stripped from the candidate pool — the Trainee slot is left for manual placement.</li>
        </ul>
        <h3 style={h3}>Processing order</h3>
        <p style={para}>
          <strong>Championship games first</strong> (so the best crews are reserved for them), then
          youngest → oldest: <em>U10 → U12 → U14 → U16 → U18 → Social</em>. Because the cheapest
          eligible referee is preferred for whatever's being filled, processing youngest-first means
          the <strong>cheapest referees land on the youngest divisions</strong>.
        </p>
        <h3 style={h3}>Hard constraints (a person is excluded if…)</h3>
        <ul style={ul}>
          <li>Outside their availability window.</li>
          <li>Their assigned team (People → Availability → Team Assignments) is playing at that time.</li>
          <li>They are excluded from one of the game's teams (People → Availability → Team Exclusions).</li>
          <li>They're already assigned in that time slot — shared across roles, so <strong>nobody is on bench while a referee, or vice-versa</strong>.</li>
          <li>They hold no qualification with a non-zero pay rate for that game's band (a blank pay band = not eligible).</li>
        </ul>
        <h3 style={h3}>Soft ranking among eligible people (tunable in Admin → AI Management)</h3>
        <ul style={ul}>
          <li><strong>Cheapest referee</strong> — cost penalty per dollar; with youngest-first processing this puts the cheapest refs on the youngest games.</li>
          <li><strong>Historical division</strong> — someone usually on U12 is preferred for U12; usual spread = preferred set.</li>
          <li><strong>Same-day clustering</strong> — a person's shifts are kept on one day where possible.</li>
          <li><strong>Prior-month fairness</strong> — each person's total tracks the prior calendar month's per-person average, so volume stays even month-to-month.</li>
          <li><strong>Female → female game</strong> — the <em>lowest-weight</em> nudge: a female referee is gently preferred for girls/women's games. Needs a Gender set on the People record; unset = ignored.</li>
        </ul>
        <p style={para}>
          Order of referee seniority (cheapest → dearest, which is how "cheapest to youngest"
          resolves): Trainee 1 → Trainee 2 → Association Community → Association Development →
          Association Intermediate → Association Advanced / Veterans 1 → State Development → State
          Grading / Veterans 2 → National Development / National.
        </p>
        <h3 style={h3}>The preview, then Apply</h3>
        <p style={para}>
          Nothing is saved on Run. The modal shows:
        </p>
        <ul style={ul}>
          <li><strong>Cost-comparison strip</strong> — three big numbers at the top: <em>Last Period</em> (actual pay across the same-length window immediately before this range), <em>Budget</em> (sum of Daily Budgets across each day-of-week in the proposed range), and <em>Proposed</em> (this run's total). Proposed colours red when it goes over Budget. Each cell also shows the underlying <strong>game count + slot count + $/slot rate</strong> so you can tell whether a big period-over-period delta is a volume issue (more slots being filled) or a rate issue (more expensive qualifications being assigned).</li>
          <li><strong>Per Person tab</strong> — fairness table: referee games, bench games, total forecast pay per person, with High / Low flags when their count is &plusmn;2 from the per-run average.</li>
          <li><strong>Per Game tab</strong> — one row per game listing the proposed Crew Chief / Referee / Trainee / Bench. Skipped games (locked / has confirmed attendance) are flagged inline so it's obvious why nothing was changed.</li>
          <li><strong>Export CSV / Print &amp; PDF</strong> — buttons on the right of the tab bar export whichever tab is currently visible. CSV downloads a spreadsheet-friendly file (one row per game / per person); Print &amp; PDF opens a print-ready view with the cost summary at the top — use your browser's <em>Save as PDF</em> option for a file, or just hit Print to send it to a printer.</li>
          <li><strong>Side-by-side per-day breakdown vs same day last week</strong> — collapsible debug table just below the cost / counts strips. Each row pairs a proposed day with the SAME day-of-week from the previous week (Mon ↔ Mon, Tue ↔ Tue) so the comparison is apples-to-apples. The Δ Cost and Δ Slots columns let you spot which days drive a delta and whether it's volume (more slots being filled) or rate (more expensive qualifications). The top-of-modal "Last Period" cell uses immediately-before instead, which can be misleading when the proposed range and last-period range cover different days of the week. A <strong>$0 Slots</strong> column on the last-week side flags slots where someone was assigned but the qualification on file pays $0 for the game's division — those slots make last week look cheaper than it really should be, and they're the most common cause of "AI Allocate is more expensive than last week even with fewer slots".</li>
          <li><strong>"Couldn't make"</strong> list — for every empty seat, a plain-English reason ("6 already on another game in this slot; 2 unavailable per their availability window") so you know whether it's a pool-depth issue or a config issue.</li>
        </ul>
        <p style={para}>
          Click <em>Apply Allocation</em> to write the assignments to the database. Each game is
          updated atomically (DELETE + INSERT in one transaction) so a partial failure can never
          leave a game half-filled. Skipped games (locked, attended) are not touched.
        </p>
        <h3 style={h3}>Import Roster</h3>
        <p style={para}>
          For when an external roster (image or PDF of someone else's spreadsheet) needs to be
          applied wholesale. Upload → preview the parsed assignments → apply. Same idempotent
          DELETE+INSERT path as AI Allocate. Only visible to users with edit permission.
        </p>
        <h3 style={h3}>Daily cost bar</h3>
        <p style={para}>
          The bar at the top of the page shows total assigned cost for the day vs. the day's budget
          (Admin → Budget). Over-budget days show a warning. The bar is now permanently mounted —
          values update in place during a recalc so the page never jumps when you click an
          assignment.
        </p>
        <h3 style={h3}>Empty court columns</h3>
        <p style={para}>
          A court with no games on the selected day is dropped from the grid entirely so the
          remaining columns reflow to fill the page width. No more empty Beaton column on midweek
          days.
        </p>
      </div>
    )
  },

  {
    key: 'coach-planner',
    label: 'Coach',
    group: 'Pages',
    requires: { pageKey: 'coach-planner', sectionKey: 'coach-planner' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Coach</h2>
        <p style={para}>
          The Coach Planner is where you set up individual coaching <strong>Programs</strong>
          {' '}(specific Tuesday-night sessions, etc.), assign coaches to them, pick which
          participants attend, and tick off attendance.
        </p>
        <h3 style={h3}>Two layers — Program Type vs Program</h3>
        <ul style={ul}>
          <li><strong>Program Type</strong> (Admin → Coaching Programs) — the long-running enrolment, e.g. "U10 Skills Term 2". Has a start/end date, signup URL, weekly budget.</li>
          <li><strong>Program</strong> (this page) — one specific session of a Program Type, e.g. "U10 Skills, Tue 7pm Court 1, 2 May". This is what coaches and participants attend.</li>
        </ul>
        <p style={para}>
          A Participant is enrolled in a <em>Program Type</em> (long-term). Per-session attendance
          is recorded against an individual <em>Program</em>.
        </p>
        <h3 style={h3}>Adding a Program</h3>
        <ol style={ol}>
          <li>Click + on the day's grid (or use the <strong>+ Add Program</strong> button).</li>
          <li>Pick a Program Type, start time, duration in minutes. The <strong>End Time</strong> field auto-calculates and is read-only.</li>
          <li>Pick a Court and (optionally) an Owner who's accountable for the session.</li>
          <li>Notes appear to coaches in their portal.</li>
        </ol>
        <h3 style={h3}>Assigning coaches</h3>
        <p style={para}>
          Each Program tile has a Coaches dropdown. Coaches who are unavailable at that time, busy
          on another court, or playing in a game are flagged. You can assign multiple coaches per
          program; each one gets a tick to mark attended.
        </p>
        <p style={para}>
          Once assigned, the coach can accept or reject the shift in their My Portal. By default
          they have until <strong>24 hours before</strong> the program starts; admins can change
          this window per category in Admin → Roster Response.
        </p>
        <p style={para}>The coach pill on the tile colour-codes their state:</p>
        <ul style={ul}>
          <li><strong style={{ background: '#a5d6a7', padding: '1px 6px', borderRadius: 3 }}>Green</strong> — accepted in My Portal.</li>
          <li><strong style={{ background: '#ffcdd2', padding: '1px 6px', borderRadius: 3, color: '#c62828' }}>Red</strong> — rejected in My Portal.</li>
          <li><strong style={{ background: '#ffe0b2', padding: '1px 6px', borderRadius: 3 }}>Light orange</strong> — they've <em>seen</em> the shift in My Portal but haven't responded yet. Hover for the timestamp of when they viewed it.</li>
          <li><strong style={{ background: '#f0f0f0', padding: '1px 6px', borderRadius: 3 }}>Grey</strong> — never opened in My Portal.</li>
        </ul>
        <h3 style={h3}>Selecting participants</h3>
        <p style={para}>
          The Program lists every Participant who's enrolled in its Program Type. Use <strong>Select
          Participants</strong> to tick which ones are expected at this specific session. Once
          selected they appear as badges on the tile.
        </p>
        <h3 style={h3}>Attendance + the financial flag</h3>
        <p style={para}>
          Once the program time arrives, the checkboxes on each participant badge become tickable.
          The <strong>Participants</strong> header shows the count attended / total and a <strong>
          Select All</strong> / <strong>Deselect All</strong> button — one click marks every
          participant on the tile as attended (or clears them all). Bulk select skips the per-person
          financial-reminder prompt; flip individuals one-by-one if you need to send a reminder SMS.
        </p>
        <p style={para}>Each badge is colour-coded by financial status, and gets darker once attendance is ticked:</p>
        <ul style={ul}>
          <li><strong style={{ background: '#e8f5e9', color: '#2e7d32', padding: '1px 6px', borderRadius: 3, border: '1px solid #a5d6a7' }}>Financial</strong> — light green; turns dark green when attended.</li>
          <li><strong style={{ background: '#ffebee', color: '#c62828', padding: '1px 6px', borderRadius: 3, border: '1px solid #ef9a9a' }}>Not financial</strong> — light red; turns dark red when attended. Also shows <strong style={{ color: '#c62828' }}>$!</strong> next to the name.</li>
          <li><strong style={{ background: '#f5f5f5', color: '#555', padding: '1px 6px', borderRadius: 3, border: '1px solid #bdbdbd' }}>Unknown</strong> — light grey; turns dark grey when attended. Also shows <strong>?</strong> next to the name.</li>
        </ul>
        <p style={para}>
          Ticking attendance on a not-financial participant opens a confirmation dialog with three
          buttons:
        </p>
        <ul style={ul}>
          <li><strong>Checkin and Notify</strong> — marks them attended <strong>and</strong> sends a signup-reminder SMS with the program's signup URL.</li>
          <li><strong>Checkin</strong> — marks them attended without sending an SMS.</li>
          <li><strong>Cancel</strong> — does nothing.</li>
        </ul>
        <div style={noteBox}>
          The reminder SMS is sent to the participant's contact mobile, with the program's signup
          URL from <strong>Admin → Coaching Programs</strong>. If the program has no signup URL set,
          <em>Checkin and Notify</em> is greyed out — set the URL first, or use plain <em>Checkin</em>.
        </div>
        <h3 style={h3}>End-time auto-calculation</h3>
        <p style={para}>
          On the program form, the End Time field is read-only and live-updates from Start Time +
          Duration. The portal and the weekly roster email both display the time as
          {' '}<code style={code}>start – end</code> rather than just the start.
        </p>
      </div>
    )
  },

  {
    key: 'court-planner',
    label: 'Court',
    group: 'Pages',
    requires: { pageKey: 'court-planner', sectionKey: 'court-planner' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Court</h2>
        <p style={para}>
          The Court Planner manages court-official rostering — the front-of-house roles
          (scorers, scoreboard operators, etc.) that aren't tied to a specific game.
        </p>
        <h3 style={h3}>Adding an assignment</h3>
        <ol style={ol}>
          <li>Pick a date.</li>
          <li>Click <strong>+ Add Court Official</strong>, choose a person + their qualification.</li>
          <li>Set start and end time. Pay is calculated from the qualification's hourly rate × hours.</li>
        </ol>
        <h3 style={h3}>Attendance</h3>
        <p style={para}>
          Each row has an attendance tick. As with games, marking attended is what produces actual
          pay; forecast pay still shows on the assignment itself.
        </p>
        <h3 style={h3}>Locking days</h3>
        <p style={para}>
          Lock a day to freeze its court rostering. Same idea as the Game Planner lock — used after
          payroll runs.
        </p>
      </div>
    )
  },

  {
    key: 'rosters',
    label: 'Home',
    group: 'Pages',
    requires: [
      { pageKey: 'rosters', sectionKey: 'rosters' },
      { pageKey: 'rosters', sectionKey: 'email-rosters' }
    ],
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Home</h2>
        <p style={para}>
          Read-only view of who's working what across a week. The default landing page. Useful for
          sharing the schedule with staff or printing. Previously called "Rosters" — same page, new
          name.
        </p>
        <h3 style={h3}>Three layouts</h3>
        <ul style={ul}>
          <li><strong>By Person</strong> — every staff member with their week. Best for "what am I working?".</li>
          <li><strong>By Court</strong> — court × time grid across days. Best for venue planners. Empty courts (no games anywhere in the visible week) are hidden so the grid stays tidy.</li>
          <li><strong>By Day</strong> — full day rosters laid out vertically. Best for printing. Same empty-court hiding.</li>
        </ul>
        <h3 style={h3}>Up Next</h3>
        <p style={para}>
          The <em>Up Next</em> strip sits at the top of both the <strong>by-court</strong> and
          <strong> by-day</strong> views. It shows the next upcoming game on each court across the
          remaining days of the week. Live games are flagged with a red <strong>LIVE</strong> badge.
        </p>
        <h3 style={h3}>Final scores + winning team</h3>
        <p style={para}>
          Once both team scores are imported, every layout (by-person, by-court, by-day, Up Next)
          renders the matchup as <em>TeamA (score) vs TeamB (score)</em> with the winning team in
          <strong> bold</strong>. Tied games and games still missing a score render at the default
          weight. Same formatting as the Game Planner so the result is obvious wherever you look.
        </p>
        <h3 style={h3}>Past days roll-up (Day view)</h3>
        <p style={para}>
          On the Day view, days that have already passed within the current week are rolled up
          behind a <em>Show past day(s) this week (N)</em> toggle. Today and future days are always
          visible. Click the toggle to expand the past days if you need to review yesterday.
        </p>
        <h3 style={h3}>Pay totals</h3>
        <p style={para}>
          If <em>Pay visibility</em> is on (Admin → Settings), each game cell totals its forecast
          pay. The by-person view shows the staff member's weekly forecast and attended pay totals.
        </p>
      </div>
    )
  },

  {
    key: 'people',
    label: 'People',
    group: 'Pages',
    requires: [
      { pageKey: 'admin', sectionKey: 'person' },
      { pageKey: 'admin', sectionKey: 'availability' },
      { pageKey: 'admin', sectionKey: 'participants' }
    ],
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>People</h2>
        <p style={para}>
          The People page is split into tabs covering <strong>People</strong> (your staff:
          referees, coaches, court officials), <strong>Availability</strong>, and{' '}
          <strong>Participants</strong> (athletes signed up to coaching programs).
        </p>

        <h3 style={h3}>People tab</h3>
        <p style={para}>
          One row per person. Click a name to edit; the form has name, email, phone, and bank
          details (used for ABA payroll exports). Bank fields are masked (`****1234`) — to leave
          them unchanged on update, simply don't retype them. Typing new digits replaces them.
        </p>
        <ul style={ul}>
          <li><strong>+ Add Person</strong> — choose <strong>Select from BasketballConnect</strong> (search the BasketballConnect members who are not yet in People; their name, email and mobile come across and stay linked) or <strong>Add manually</strong>.</li>
          <li><strong>Copy Link</strong> — copies the person's My Portal availability URL to your clipboard.</li>
          <li><strong>Email Link</strong> — sends them the same URL by email.</li>
          <li><strong>Reset MFA</strong> — clears the person's MFA record (any active SMS code is wiped, the verified flag cleared). On their next portal visit they'll need to verify by SMS again. Use if their mobile changes or you suspect their cookie has been compromised. To also boot every browser they're already signed in on, rotate the availability token (the existing 30-day cookies become useless because they're keyed to the person, but a token rotation also forces them to re-open via a fresh link).</li>
        </ul>

        <h3 style={h3}>Availability tab</h3>
        <p style={para}>
          Per-person weekly availability windows (e.g. Mon 4pm–10pm). Used by the Game Planner to
          filter dropdowns and by the AI Allocator to score candidates. Most staff set their own
          availability through My Portal → Availability — admins only edit it for fill-ins or
          corrections.
        </p>
        <h3 style={h3}>Time Off</h3>
        <p style={para}>
          Below the weekly times on a person's <strong>Availability</strong> tab (and on <strong>My Portal →
          Availability</strong>, where people book their own) is <strong>Time Off</strong>: tick <em>Day(s) off</em> for one
          day or a run of days (e.g. 3 Nov, or 3 to 5 Nov), or <em>Part of a day</em> for a period (e.g. 3 Nov 9am to 5pm). It saves as you
          add or remove it. Nobody can be rostered inside their time off: AI Allocate skips them, and the Game Planner,
          Coach Planner and Court planner list them as unavailable (tagged <em>Time off</em>) and stop the Court planner adding them.
        </p>
        <p style={para}>
          <strong>If they are already rostered then</strong>, saving the time off first lists the games, coaching sessions and court
          shifts it clashes with and asks them to confirm. Confirming <strong>rejects</strong> those shifts (exactly as if they had
          pressed the cross in My Portal, so they show red for you to replace; coaching program owners get the usual decline text).
          Shifts inside the My Portal cut-off, already started, or on a locked day cannot be rejected automatically: the person is told to
          contact you, and they stay on the roster until you deal with them.
        </p>
        <h3 style={h3}>Shift Reliability report (Reports)</h3>
        <p style={para}>
          <strong>Reports → Shift Reliability</strong> shows, for a date range (and optionally games, coaching or court only), who turns up for
          their shifts and who does not: attended, no-shows, rejected (and how many were rejected because of time off) and a turn-up %.
          Click a person to see each rejected shift and no-show; Export CSV downloads the table. The report is built from a permanent record
          that is never deleted, even if the shift is later removed from the roster: <strong>rejections</strong> are recorded the moment they happen
          (My Portal or time off), and <strong>attended / no-show</strong> are recorded when a planner day is locked, using the attendance ticks at that point.
          Existing rejections and already-locked days were added to the record when this feature went live.
        </p>
        <h3 style={h3}>Team Assignments and Team Exclusions</h3>
        <p style={para}>
          Both lists sit on a person's <strong>Availability</strong> tab (People → Edit) and on <strong>My Portal →
          Availability</strong>, where people can edit their own. They save as you add or remove a team.
        </p>
        <ul style={ul}>
          <li><strong>Team Assignments</strong> — the teams the person plays for. They are not rostered in a timeslot while one of those teams is playing. For anyone linked to BasketballConnect they are added <strong>automatically</strong> from the team they are registered in (after every BasketballConnect sync, or press <strong>BC Teams</strong> in People Management), only for teams that still have games to come, and marked <em>from BasketballConnect</em>. Ones you add by hand are never changed; if you remove an automatic one it is not added back, and one BasketballConnect no longer has them in (moved team, season over) is taken off.</li>
          <li><strong>Team Exclusions</strong> — teams whose games the person must not be rostered on. It only blocks games involving that team: they can still be rostered on other games, even at the same time. AI Allocate skips them for those games, and the Game Planner lists them under the unavailable names tagged <em>(Excluded)</em>.</li>
        </ul>

        <h3 style={h3}>Active Status (per role) — Referees / Bench / Coach / Court tabs</h3>
        <p style={para}>
          Each Person row carries four boolean flags — <strong>Active Referee</strong>,{' '}
          <strong>Active Bench Official</strong>, <strong>Active Coach</strong>,{' '}
          <strong>Active Court Official</strong> — that gate them per function. The flags are
          independent of qualifications: a person needs both a relevant qualification AND the
          Active flag to appear in the matching dropdown (Game Planner ref/bench, Coach Planner
          coach, Court Planner court official) and to be considered by AI Allocate. Inactive
          roles still keep historical assignments + qualifications — the person just stops
          appearing in pickers for that function.
        </p>
        <p style={para}>
          You manage the flags in two places:
        </p>
        <ul style={ul}>
          <li><strong>Per person</strong> — open any person via People &rarr; People Management &rarr; Edit. The form has an <em>Active Status</em> section with the four checkboxes. Tick / untick and save. Adding a qualification ticks the matching Active box for you; untick it if they should not be active.</li>
          <li><strong>In bulk</strong> — the new <strong>Referees / Bench / Coach / Court</strong> tabs each show the currently-active list with a Remove button per row + an <em>+ Add Active …</em> button. The Add modal lists every person who holds a relevant qualification but isn't yet active; tick the ones you want and click <em>Add Selected</em> to mark them all active in one go.</li>
        </ul>
        <p style={para}>
          On first deploy a one-time backfill flips the flag on for anyone who's been rostered to
          that function in the <strong>last 14 days</strong> — so existing staff don't need to be
          ticked back in by hand. After the backfill the flags are purely manual; new people start
          inactive until an admin opts them in.
        </p>
        <div style={tipBox}>
          A person who's a referee but not a coach can have <em>Active Referee</em> ticked and{' '}
          <em>Active Coach</em> unticked — they'll appear on the Game Planner ref dropdown but
          never on the Coach Planner coach dropdown.
        </div>
        <div style={noteBox}>
          <strong>Active requires a qualification.</strong> You can't mark a person active for a
          role they hold no qualification in — the checkbox is greyed out (with a "no
          qualification" hint) and the backend rejects the save with a clear error if a stale
          client tries it. The same rule applies in the bulk Add modal: only people with the
          relevant qualification appear in the picker. Removing a person's last qualification of
          a given type also auto-unticks the matching Active flag in the form, so a save can't
          desync the two.
        </div>

        <h3 style={h3}>Participants tab</h3>
        <p style={para}>
          Athletes enrolled in coaching programs. The form takes External ID (optional), name,
          gender, age group, mobile, contact person (parent/guardian) name, email, notes, and a
          list of program enrolments.
        </p>
        <p style={para}>
          For each enrolment, set a <strong>Financial</strong> status:
        </p>
        <ul style={ul}>
          <li><strong>Unknown</strong> — the default; nothing flagged in the planner.</li>
          <li><strong>Yes (paid)</strong> — registered + paid, shows clean.</li>
          <li><strong>No (not paid)</strong> — flagged red in the Coach Planner with a <strong>$!</strong> badge; check-ins prompt the coach to send a signup-reminder SMS.</li>
        </ul>
        <div style={tipBox}>
          The Public Portal Programs tab also creates Participant rows — when a parent signs their
          child up, the row appears here automatically (financial status starts as Unknown).
        </div>

        <h3 style={h3}>Import participants from CSV</h3>
        <p style={para}>
          Click <strong>Import CSV</strong> (next to <em>+ Add Participant</em>) and pick a CSV
          export from your registration system (e.g. PlayHQ).
        </p>
        <p style={para}>The import reads the first 6 columns positionally:</p>
        <ol style={ol}>
          <li><strong>User ID</strong> &rarr; External ID (dedupe key — same ID = update in place)</li>
          <li><strong>First Name</strong></li>
          <li><strong>Last Name</strong></li>
          <li><strong>Gender</strong></li>
          <li><strong>Email</strong></li>
          <li><strong>Mobile Number</strong></li>
        </ol>
        <p style={para}>
          Three more columns are read by header name (case-insensitive):
        </p>
        <ul style={ul}>
          <li><code style={code}>Competition/Program</code> &rarr; matched against existing Coaching Programs (Admin &rarr; Coaching Programs); created on the fly with that name if it doesn't exist.</li>
          <li><code style={code}>Registration Divisions</code> &rarr; (a) appended to the participant's Notes as a structured marker <code style={code}>[program: NAME | divisions: ...]</code>, AND (b) <strong>parsed for a day-of-week + start time</strong> (e.g. <em>"Elite Hawks Rep Monday 6am - 7am Term 2"</em> &rarr; Monday 06:00). When parsed, the participant is auto-attached <strong>only</strong> to upcoming sessions of that program on that specific day at that specific time. If the cell has no recognisable day/time, they're attached to every upcoming session as before.</li>
          <li><code style={code}>Payment Status</code> &rarr; drives the Financial flag on this enrolment. <strong>Registered</strong> = Paid (green badge); anything else (or blank) = Not Paid (red badge). The import is the source of truth — re-importing overwrites the previous value.</li>
        </ul>
        <div style={tipBox}>
          <strong>Multi-day registrations:</strong> if a participant attends multiple sessions of
          the same program (e.g. Mon + Fri), they have one CSV row per division. Each row is parsed
          independently — the participant ends up attached only to the matching sessions, not to
          every day. Across rows for the same person, payment status is OR-merged: if <em>any</em>
          row says Registered, the participant is Paid for the whole program.
        </div>
        <div style={tipBox}>
          <strong>Sessions created later automatically pick up the right participants.</strong>
          Each parsed division is saved as a session-target (day-of-week + start time). When a new
          Program is created in the Coach Planner, only participants whose target matches the new
          session's day + time are attached — participants without any targets (e.g. added via
          People page) keep being attached to <em>every</em> session as before.
          <br /><br />
          <em>Implication:</em> if you import a CSV that says someone attends Mondays but no
          Monday Program exists yet, nothing visible happens at import time — but as soon as you
          create the Monday session, they appear on it. If you create a Tuesday session, they
          don't.
        </div>
        <div style={tipBox}>
          <strong>Manual entries override targets.</strong> Editing a participant via People
          &rarr; Participants and saving treats them as "global" — all CSV-derived session targets
          for that participant are cleared. Use this when someone is registered for additional
          unscheduled sessions that you don't want filtered out by their CSV target.
        </div>
        <div style={tipBox}>
          The import results panel shows how many rows targeted a specific session vs. fell back to
          attach-all (no day/time parsed). Use that to spot rows whose Registration Divisions text
          isn't being recognised — usually it's a different word for the day or a missing time.
        </div>
        <div style={noteBox}>
          When a Coaching Program ends (its <em>End Date</em> passes and the daily auto-archive
          fires, or you manually archive it), the matching <code style={code}>[program: NAME |
          divisions: ...]</code> line is automatically stripped from every participant's notes.
          Detail for active programs is preserved.
        </div>
      </div>
    )
  },

  {
    key: 'message',
    label: 'Message (SMS)',
    group: 'Pages',
    requires: [
      { pageKey: 'message', sectionKey: 'send-roster' },
      { pageKey: 'message', sectionKey: 'custom' },
      { pageKey: 'message', sectionKey: 'bulk-role' },
      { pageKey: 'message', sectionKey: 'bulk-participants' }
    ],
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Message (SMS)</h2>
        <p style={para}>
          The Message tab is the home for outbound SMS. All credentials and the sender ID live
          under <strong>Admin → SMS</strong> — set those up first (see the SMS Configuration help
          topic).
        </p>
        <h3 style={h3}>SMS one or more people their roster</h3>
        <ol style={ol}>
          <li>Choose a period: <em>Just this day</em> or <em>7-day week</em>.</li>
          <li>Pick a date.</li>
          <li>Search and tick everyone you want to message. <em>Select all visible</em> ticks everyone in the current filter who has a phone on file.</li>
          <li>Click <strong>Send Roster SMS</strong>.</li>
        </ol>
        <p style={para}>
          Each recipient gets their own per-person message body — their assignments + a link to
          their My Portal page. People without a valid Australian mobile are skipped and listed in
          the result.
        </p>

        <h3 style={h3}>Custom SMS to selected people</h3>
        <p style={para}>
          Same recipient picker, plus a free-text message. Personalise with{' '}
          <code style={code}>{'{first_name}'}</code> or <code style={code}>{'{name}'}</code>.{' '}
          <code style={code}>{'{optout}'}</code> is appended automatically by Mobile Message.
        </p>

        <h3 style={h3}>Bulk SMS by role + date range</h3>
        <p style={para}>
          Pick a role (Referees / Bench Officials / Coaches / Court Officials) and a date range.
          The preview button shows everyone who was assigned that role within the range so you can
          confirm the audience before sending.
        </p>

        <h3 style={h3}>Bulk SMS to participants</h3>
        <p style={para}>
          Filter by Program Type, Age Group, name search — or any combination. Empty filters mean
          "all participants". Use <code style={code}>{'{contact_name}'}</code> to address the
          parent.
        </p>

        <h3 style={h3}>What you get back</h3>
        <p style={para}>
          Every send shows: recipients sent, total cost in credits, and the post-send credit balance.
          When the balance drops below the configured threshold (Admin → SMS), a popup warns to top
          up credits ASAP.
        </p>
        <div style={noteBox}>
          Every send is recorded in <strong>Admin → Audit</strong> under category{' '}
          <code style={code}>SMS</code> with the recipient count, total cost, and a 80-char excerpt
          of the message body.
        </div>
      </div>
    )
  },

  {
    key: 'reports',
    label: 'Reports',
    group: 'Pages',
    requires: { pageKey: 'reports', sectionKey: 'reports' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Reports</h2>
        <p style={para}>
          Aggregate reports across staff and date ranges.
        </p>
        <h3 style={h3}>Pay report</h3>
        <p style={para}>
          For a given date range, lists every assignment per person (referee, bench, court, coach)
          with forecast and actual pay. Forecast = pay for the assignment as scheduled. Actual = pay
          for assignments where the person was marked attended. Filter by person, qualification
          type, or show forecast/actual.
        </p>
        <h3 style={h3}>Coaching allocation</h3>
        <p style={para}>
          Per-coach summary across a date range — programs allocated, hours, total pay.
        </p>
        <h3 style={h3}>Participant attendance</h3>
        <p style={para}>
          Per-participant attendance counts across a date range. Useful for end-of-term reports to
          parents or association compliance.
        </p>
        <h3 style={h3}>Session participation</h3>
        <p style={para}>
          Per-session detail across a date range. One row per Session with:
        </p>
        <ul style={ul}>
          <li><strong>Allocated</strong> — how many participants were on the session's tile.</li>
          <li><strong>Attended</strong> — how many were ticked off as actually present.</li>
          <li><strong>Paid / Not paid / Unknown</strong> — financial breakdown of the <em>attended</em> participants (the colour matches the Coach Planner badges).</li>
          <li><strong>Targeted</strong> — participants whose CSV target matches this session's day + time (i.e. they were expected here).</li>
          <li><strong>Other session</strong> — participants enrolled in this program but normally on a different day/time. They were added via <em>Select Other Participants</em>.</li>
          <li><strong>Global</strong> — participants with no session targets at all (manual add via People or My Portal Programs).</li>
        </ul>
        <p style={para}>
          Click <em>Details</em> on any session to expand a per-participant list. Save as PDF or
          Excel — the Excel export includes the per-participant rows underneath each session for
          full traceability.
        </p>
      </div>
    )
  },

  {
    key: 'payroll',
    label: 'Payroll',
    group: 'Pages',
    requires: [
      { pageKey: 'payroll', sectionKey: 'payroll-maintenance' },
      { pageKey: 'payroll', sectionKey: 'process-payroll' },
      { pageKey: 'payroll', sectionKey: 'payroll-report' }
    ],
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Payroll</h2>
        <p style={para}>
          Process payroll for a Mon–Sun week. Outputs an ABA file (the Australian banking format
          your business bank account accepts as a single import for batch payments).
        </p>
        <h3 style={h3}>Process a week</h3>
        <ol style={ol}>
          <li>Pick a Monday-anchored week.</li>
          <li>The page lists every person who worked anything that week + their attended pay.</li>
          <li>Tick anyone you want to exclude (e.g. paid manually).</li>
          <li><strong>Process Payroll</strong> creates a payroll run record and generates the ABA file. Download the file from your browser and upload to your bank.</li>
        </ol>
        <div style={warnBox}>
          Once a week is processed it's recorded as a payroll run. Use <strong>Lock Week</strong>{' '}
          to prevent any further attendance changes feeding back into payroll. Locking is what
          guarantees the report you exported matches what your bank will pay.
        </div>
        <h3 style={h3}>Business banking details</h3>
        <p style={para}>
          The ABA file needs your association's bank details (BSB, account, lodgement reference,
          APCA number, etc.) — set these once in <strong>Admin → Business Banking</strong>.
        </p>
      </div>
    )
  },

  // ============ Admin sub-pages ============
  {
    key: 'admin-games',
    label: 'Games (admin)',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'games' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Games</h2>
        <p style={para}>
          A flat list of every game in the database. Use this to:
        </p>
        <ul style={ul}>
          <li>Add a one-off game manually that isn't in the upstream feed.</li>
          <li>Force a fresh import from the API (red button — same as the Game Planner's force button).</li>
          <li>Search by team, date, court, division.</li>
        </ul>
      </div>
    )
  },
  {
    key: 'admin-settings',
    label: 'Settings',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'settings' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Settings</h2>
        <h3 style={h3}>Game Time Offset</h3>
        <p style={para}>
          Hours from UTC for displaying game times (Sydney AEDT = +11, AEST = +10). Update twice a
          year for daylight saving.
        </p>
        <h3 style={h3}>Pay Visibility</h3>
        <p style={para}>
          Master switch for whether staff see pay information in their My Portal page and roster
          emails. Admin pages always show pay regardless.
        </p>
        <h3 style={h3}>Sync Teams from Upstream</h3>
        <p style={para}>
          Pulls the team-name list from the upstream sport API and rewrites any games in the DB
          showing a <code style={code}>Team {'{id}'}</code> placeholder with the real team name. Pick the smallest date window that
          still includes your teams (smaller responds faster). The cron importer also runs this in
          the background — manual sync only needed when you've just imported a fresh competition.
        </p>
      </div>
    )
  },
  {
    key: 'admin-budget',
    label: 'Budget',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'budget' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Budget</h2>
        <p style={para}>
          Daily spending ceilings per day of the week. The Game Planner shows a daily-cost bar
          comparing actual cost vs. budget; over-budget days are flagged.
        </p>
        <ul style={ul}>
          <li><strong>Budget Amount</strong> — dollar ceiling.</li>
          <li><strong>Alert Threshold</strong> — when to start visually warning (80% / 100% / 120%).</li>
        </ul>
      </div>
    )
  },
  {
    key: 'admin-audit',
    label: 'Audit log',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'audit' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Audit</h2>
        <p style={para}>
          Every state-changing action in the platform is logged here with: who did it, when, the
          action description, the category, and (where applicable) old/new value JSON for diffs.
        </p>
        <p style={para}>
          Common categories worth filtering on:
        </p>
        <ul style={ul}>
          <li><strong>Admin</strong> — admin-page configuration changes.</li>
          <li><strong>Game Planner / Court Planner / Coach Planner</strong> — assignment + attendance changes.</li>
          <li><strong>Attendance</strong> — game attendance ticks.</li>
          <li><strong>Payroll</strong> — every payroll run.</li>
          <li><strong>SMS</strong> — every SMS send (test, roster, custom, bulk, reminder).</li>
          <li><strong>Public Signup</strong> — participants allocated through the My Portal Programs tab.</li>
        </ul>
      </div>
    )
  },
  {
    key: 'admin-ai-management',
    label: 'AI Management',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'ai-management' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → AI Management</h2>
        <p style={para}>
          Tunes the AI Allocate algorithm. Every weight, threshold, and structural rule the
          allocator uses lives here — change a value, click Save, and the next AI Allocate run
          uses the new config. No restart needed.
        </p>

        <h3 style={h3}>Scoring rules</h3>
        <p style={para}>
          Each scoring rule contributes to the per-candidate score. The algorithm sums every
          enabled rule, then ranks candidates by total score. Highest score wins the slot.
        </p>
        <ul style={ul}>
          <li><strong>Order</strong> — up/down arrows. Purely visual; the algorithm sums every enabled rule, but the list is shown top-to-bottom as priority for human readability.</li>
          <li><strong>Weight</strong> — number. Positive = bonus, negative = penalty. Higher absolute value = stronger contribution.</li>
          <li><strong>Enabled</strong> — checkbox. When off the rule contributes 0.</li>
        </ul>
        <p style={para}>Default scoring rules (rule set v2, 16 May 2026):</p>
        <ul style={ul}>
          <li><strong>Base score</strong> — flat starting score every eligible candidate gets (default 100).</li>
          <li><strong>Cost penalty per dollar</strong> — subtracts <em>(per-game pay × weight)</em> so the cheapest eligible referee wins for the division being filled (default 2). With youngest-first processing this is the "cheapest → youngest" rule.</li>
          <li><strong>Historical division match (max bonus)</strong> — scaled bonus for someone usually assigned to this division (default 30).</li>
          <li><strong>Same-day clustering bonus</strong> — bonus if the person already has a shift on this date in this run, so their shifts stay on one day (default 25).</li>
          <li><strong>Prior-month fairness penalty</strong> — subtracted per assignment they're over the prior calendar month's per-person average (default 12).</li>
          <li><strong>Female referee → female game</strong> — the smallest weight; a gentle tie-breaker only (default 5). Needs a Gender on the People record.</li>
        </ul>

        <h3 style={h3}>Limits & thresholds</h3>
        <ul style={ul}>
          <li><strong>Default referees / bench per game</strong> — 2 and 1.</li>
          <li><strong>Championship people per game</strong> — any division whose name contains "Championship" gets this many referees AND bench (default 3).</li>
          <li><strong>Playing-conflict window (min)</strong> — a person whose assigned team plays within ±N minutes is excluded (default 30).</li>
          <li><strong>Prior-month lookback</strong> — calendar months back for the fairness average (default 1).</li>
          <li><strong>History lookback (months)</strong> — window for the historical-division match (default 3).</li>
          <li><strong>Exclude trainees</strong> + <strong>Trainee regex</strong> — trainees are never auto-assigned; any level matching the regex (default <code style={code}>trainee</code>) is dropped from the pool. Place trainees manually.</li>
        </ul>
        <div style={noteBox}>
          <strong>Processing order is fixed</strong> by the rules — Championship first, then
          youngest → oldest (U10 → … → Social) — and is no longer configurable. The hard
          constraints (team-playing, no bench+ref overlap, must hold a paying qual) are rules,
          not toggles. Only the soft weights above are tunable.
        </div>

        <h3 style={h3}>Save / Reset</h3>
        <p style={para}>
          <strong>Save Changes</strong> persists the entire config to a single Settings row
          (<code style={code}>AI_ALLOCATE_CONFIG_v1</code>). Audit-logged with the new blob.{' '}
          <strong>Reset to defaults</strong> wipes the row so the loader returns the baked-in
          defaults on the next call. The "Show baked-in defaults" expandable at the bottom shows
          the current default JSON for reference.
        </p>

        <h3 style={h3}>Permissions</h3>
        <p style={para}>
          Permission key <code style={code}>admin / ai-management</code>. Read = see the tab;
          Edit = save changes / reset. Defaults to <em>hide</em> on existing profiles; grant via
          Admin → Security Profiles.
        </p>
      </div>
    )
  },
  {
    key: 'admin-referee-codes',
    label: 'Referee Codes',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'referee-codes' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Referee Codes</h2>
        <p style={para}>
          Configures the small role chip that appears beside each referee slot on the Game Planner
          (and read-only beside the referee's name on My Portal + Roster View). Defaults are
          <strong> CC</strong> (Crew Chief), <strong>R</strong> (Referee), <strong>T</strong> (Trainee).
        </p>

        <h3 style={h3}>How it works</h3>
        <ul style={ul}>
          <li>Each row is one chip. Columns: <strong>Order</strong> (controls cycle order), <strong>Code</strong> (short label, ≤10 chars), <strong>Label</strong> (descriptive — shown on chip hover).</li>
          <li>Add / rename / archive freely. Archiving keeps existing assignments untouched (slots that referenced the archived code keep displaying it until an admin cycles past it).</li>
          <li>New referee slots are auto-tagged with the code at <em>displayOrder = slotOrder</em> — so out-of-the-box, slot 1 → CC, slot 2 → R, slot 3 → T. Admins can change a slot's code by clicking the chip on the Game Planner (cycles to the next code).</li>
          <li>The cycle order on the Game Planner follows this list's <em>displayOrder</em> ascending, then wraps from the last back to the first.</li>
        </ul>

        <h3 style={h3}>Where the code shows</h3>
        <ul style={ul}>
          <li><strong>Game Planner</strong> — clickable chip before each referee dropdown. Click to cycle.</li>
          <li><strong>My Portal</strong> (Game tab + Summary) — read-only chip prefix (e.g. "CC Referee").</li>
          <li><strong>Roster View</strong> — read-only prefix in the referee list (e.g. "CC Alice, R Bob, T Charlie").</li>
        </ul>

        <h3 style={h3}>Permissions</h3>
        <p style={para}>
          Permission key <code>admin / referee-codes</code>. Read = see + open the tab; Edit = add /
          rename / archive. Profiles with <em>edit</em> on <code>admin / audit</code> automatically
          got <em>edit</em> on this key on the 16 May 2026 release; new profiles need explicit grant
          via Admin → Security Profiles.
        </p>
      </div>
    )
  },
  {
    key: 'admin-error-log',
    label: 'Error Log',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'error-log' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Error Log</h2>
        <p style={para}>
          A live record of every server error the platform captures — both errors a route
          handler returned explicitly (HTTP 500 with detail) and ones that escaped to the
          global Express error middleware. Each row carries enough context to diagnose
          and fix without needing to spelunk through console logs.
        </p>

        <h3 style={h3}>What's captured per error</h3>
        <ul style={ul}>
          <li><strong>capturedAt</strong> — server timestamp of when the error was raised.</li>
          <li><strong>source</strong> — route + method (e.g. <code>POST /api/integrations/bc/mappings</code>) or a custom string the caller supplied.</li>
          <li><strong>message</strong> — the error's <code>.message</code>.</li>
          <li><strong>stack</strong> — the JS stack trace, expandable in the row detail.</li>
          <li><strong>requestBodyPreview</strong> — first ~2KB of the JSON body that triggered the error. Useful for repro.</li>
          <li><strong>userEmail / userName</strong> — pulled from the audit headers (<code>X-Audit-User-Email</code>, <code>X-Audit-User-Name</code>) or the JWT.</li>
          <li><strong>statusCode</strong> — HTTP status returned (typically 500).</li>
        </ul>

        <h3 style={h3}>Filtering + workflow</h3>
        <ul style={ul}>
          <li>Default view: <strong>open</strong> errors only, newest first.</li>
          <li>Toggle to <em>Resolved</em> or <em>All</em> via the buttons at the top.</li>
          <li>Click any row to expand a detail panel with the full stack, body, and user info.</li>
          <li><em>Resolve…</em> opens a note field — record what the cause was + what you did. Mark resolved → row keeps for history but moves out of the open queue.</li>
          <li><em>Reopen</em> on a resolved row clears the resolution stamp.</li>
          <li><em>Delete</em> permanently removes the row (audit log keeps a record of the delete itself).</li>
        </ul>

        <h3 style={h3}>What it does NOT capture</h3>
        <ul style={ul}>
          <li><strong>Frontend</strong> JS errors. Those still go to your browser console.</li>
          <li><strong>4xx</strong> errors that the route handler returned cleanly (e.g. validation 400s aren't captured — only 500s).</li>
          <li><strong>Network-level</strong> failures before Express even sees the request (Azure App Service stops, DB pool init failure, etc.). For those, check the App Service log stream or BCSettings → Status section.</li>
        </ul>

        <h3 style={h3}>Tip — use it as a TODO queue</h3>
        <p style={para}>
          When a customer reports "something's broken", check the open queue first. The
          row will usually point straight at the offending route + body. Once you've
          shipped the fix, mark resolved with the commit hash in the note so the link
          back to the change is one click away.
        </p>
      </div>
    )
  },
  {
    key: 'admin-coaching-programs',
    label: 'Coaching Programs',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'program-types' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Coaching Programs</h2>
        <p style={para}>
          The long-running enrolments — what participants sign up for. Each Coaching Program (also
          called a "Program Type") has:
        </p>
        <ul style={ul}>
          <li><strong>Name</strong> + optional Code (used in shorthand).</li>
          <li><strong>Owners</strong> — one or more staff members accountable for the program. Tick all that apply on the form.</li>
          <li><strong>Weekly Schedule</strong> — one or more weekly recurring sessions (day-of-week + start time + duration + court). Each row also lets you pick up to <strong>5 default coaches</strong> (each = person + qualification level). When you save the Coaching Program, sessions are auto-created in the Coach Planner for every matching weekday between Start Date and End Date, and the picked coaches are auto-allocated to each new session at their selected qualification level.</li>
          <li><strong>Editing the schedule syncs future sessions</strong>:
            <ul style={ul}>
              <li>Sessions whose (day, start time, court) still match a row in the new schedule are <em>kept</em>. Their duration is updated if you changed it, and any new default coaches you added are inserted (existing manual coach assignments are never removed).</li>
              <li>Future sessions whose (day, start time, court) no longer match any schedule row are <em>safely deleted</em> — but only if no participant or coach has been marked attended yet. Sessions with attendance are preserved untouched, so admin can clean up by hand if needed.</li>
              <li>Schedule rows that don't yet have a matching session get materialized as new sessions.</li>
              <li>Past sessions are never touched.</li>
            </ul>
            The success message after saving reports how many sessions were updated, how many were removed, and how many attended sessions were preserved.
          </li>
          <li><strong>Weekly Budget</strong> — used by reports for cost-vs-budget tracking.</li>
          <li><strong>Signup URL</strong> — your external registration link (TeamApp, Squadi, Trybooking, etc.). Sent via SMS when someone allocates a participant from My Portal, and used by the not-financial reminder prompt in the Coach Planner.</li>
          <li><strong>Start Date / End Date</strong> — the program's active window.</li>
          <li><strong>Linked / Feeder Programs</strong> — see below.</li>
        </ul>

        <div style={tipBox}>
          <strong>Coach decline notifications:</strong> when an assigned coach rejects a session
          from My Portal, every Owner of that Coaching Program with a mobile number on file
          automatically receives an SMS so they can reassign. The message includes the coach's
          name, the program, and the session date/time.
        </div>

        <h3 style={h3}>Linked / Feeder Programs</h3>
        <p style={para}>
          On each Coaching Program you can tick other Coaching Programs as <strong>feeders</strong>.
          When B is a feeder of A, every participant linked to B also appears in A's sessions for
          check-in — alongside A's own participants. Useful for combined groups (e.g. include
          "Term 2 Squad" participants in a "Friday Combined Session" tile).
        </p>
        <ul style={ul}>
          <li>Adding a feeder <strong>backfills</strong> attendance for all upcoming sessions of
              the parent program — feeder participants appear immediately.</li>
          <li>Allocating a new participant to the feeder program also auto-attaches them to
              upcoming sessions of every parent program.</li>
          <li>Removing a feeder doesn't strip past attendance — it just stops new auto-attaches.</li>
          <li>Self-loops are blocked at the database level.</li>
        </ul>

        <h3 style={h3}>Auto-archive</h3>
        <p style={para}>
          A daily cron flips <em>isArchived</em> = true at 00:05 UTC for any program whose end date
          is in the past. Archived programs:
        </p>
        <ul style={ul}>
          <li>Disappear from the My Portal Programs signup picker.</li>
          <li>Disappear from the People → Participants enrolment options (admin form).</li>
          <li>Stop appearing in newly created Programs (sessions) on the Coach Planner.</li>
          <li>Existing enrolments and sessions remain in the database — only new ones are blocked.</li>
        </ul>
        <p style={para}>
          Tick <em>Show Archived</em> on the table to see and manage archived programs.
        </p>
      </div>
    )
  },
  {
    key: 'admin-bc-integration',
    label: 'BC Integration',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'settings' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → BC Integration</h2>
        <p style={para}>
          One-time setup for the Basketball Connect (Squadi) integration. Stores the admin
          email, password and TOTP seed encrypted at rest, then logs in on demand and
          re-logins automatically when a session token expires. Day-to-day work — mapping
          BC competitions to SwishIQ Coaching Programs and triggering syncs — lives on its
          own tab (<strong>Admin → BC Registrations</strong>).
        </p>

        <h3 style={h3}>Auto-login credentials</h3>
        <p style={para}>
          Three fields:
        </p>
        <ul style={ul}>
          <li><strong>Email</strong> — the BC admin user the integration logs in as.</li>
          <li><strong>Password</strong> — the BC admin password.</li>
          <li><strong>TOTP seed</strong> — the secret string behind the QR code in your
              authenticator app (e.g. <code>GRIWMZZYJM</code>). The server uses it to
              compute a fresh 6-digit code on every login. Reveal it from your authenticator
              app's "scan QR / manual entry" view, or from BC's two-factor settings page.</li>
        </ul>
        <p style={para}>
          All three are encrypted at rest with the <code>BC_ENCRYPTION_KEY</code> env var.
          If that key ever changes (e.g. App Service restored from a different config),
          the existing ciphertexts can't be decrypted — you'll get a "Failed to decrypt
          setting" error and need to re-save the credentials.
        </p>

        <h3 style={h3}>Login &amp; save fresh token</h3>
        <p style={para}>
          Performs a real BC login (TFA included) and persists the resulting session token
          encrypted in the same store. Subsequent BC API calls use that token automatically.
          Run this once after first saving credentials; auto-relogin handles every refresh
          afterwards.
        </p>

        <h3 style={h3}>Manual session token override</h3>
        <p style={para}>
          For testing or emergency use: paste a hex token captured from a logged-in BC
          admin browser tab (<code>localStorage.token</code>). Also exposes the
          organisation key and yearRefId fields. Defaults are Illawarra Basketball + the
          current season; only change them if you're integrating a different org.
        </p>

        <h3 style={h3}>Test connection</h3>
        <p style={para}>
          Calls the live BC competition-list endpoint and reports back: number of
          competitions / fee rows, a sample competition name, and any upstream error
          (with status code + body). Use this to verify auth before mapping anything.
        </p>
      </div>
    )
  },
  {
    key: 'admin-competition-mapping',
    label: 'BC Registrations',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'competition-mapping' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → BC Registrations</h2>
        <p style={para}>
          Pair each Basketball Connect competition (or specific registration group /
          division) with a SwishIQ Coaching Program. The integration sync runs automatically
          at <strong>5am, 9am, 12pm and 3pm AEDT</strong>; every player whose BC
          registration falls inside a mapping's scope is attached to every upcoming
          session of the mapped program. Use <strong>Sync now</strong> on a row to run
          an immediate ad-hoc sync (e.g. after adding a mapping or fixing an issue).
        </p>

        <h3 style={h3}>Add mapping</h3>
        <p style={para}>
          Click <em>Load BC competitions</em> first — it fetches the live list from BC
          (one entry per competition × division pair so you can map e.g.
          "Mini Hawks Term 1 2026 - Monday 1045" separately from
          "Mini Hawks Term 1 2026 - Wednesday 1045"). Then pick:
        </p>
        <ul style={ul}>
          <li><strong>BC competition / division</strong> — the dropdown shows
              "<em>Competition Name - Division Name</em>". If a competition has no
              division (whole-comp), only the competition name appears.</li>
          <li><strong>Coaching Program</strong> — pick an existing one OR
              <em>+ Create new program (from BC name)</em>. The new-program option
              opens an inline form with extra fields (see below).</li>
          <li><strong>Auto-sync daily</strong> — when ticked, the four scheduled
              syncs include this mapping. Untick to keep the mapping but only run
              it manually via <em>Sync now</em>.</li>
        </ul>

        <h3 style={h3}>Create new program inline</h3>
        <p style={para}>
          Picking <em>+ Create new program</em> turns the form into a one-click setup
          that handles the chicken-and-egg (no need to pre-create the SwishIQ program).
          Fields:
        </p>
        <ul style={ul}>
          <li><strong>Program name</strong> — pre-filled from the BC name; editable.
              If a program with that name already exists (case-insensitive), the
              mapping links to the existing one instead of creating a duplicate.</li>
          <li><strong>Location</strong> (optional) — set on the auto-seeded weekly
              template AND every generated session.</li>
          <li><strong>Default coaches</strong> — multi-select of active SwishIQ coaches.
              Attached to the weekly template AND to every generated session as
              <code>ProgramCoachAssignments</code> rows.</li>
          <li><strong>Generate sessions through</strong> (optional date) — for every
              occurrence of the parsed weekday between today and that date, a
              <code>Programs</code> row is created with the right time, location,
              coaches, and existing matching participants auto-attached. Idempotent:
              re-saving skips any week where a Program already exists at the same
              (programType, startDateTime, location).</li>
        </ul>
        <p style={para}>
          The weekly template's day + time is parsed from the BC division name. Patterns
          handled: <code>Friday 7am - 8am</code> → Fri 07:00–08:00 (60 min);
          <code>Monday 1045</code> → Mon 10:45 (60 min default);
          <code>Saturday 10am to 12pm</code> → Sat 10:00–12:00 (120 min);
          <code>Wednesday 6am - 7am</code> → Wed 06:00–07:00 (60 min). If the
          division name has no parseable day/time (e.g. "SJL Fees"), no template is
          auto-seeded — set the schedule manually under the program if needed.
        </p>

        <h3 style={h3}>Mappings table</h3>
        <p style={para}>
          Each row shows the BC competition + division, the linked Coaching Program,
          the auto-sync toggle, the last run timestamp + status, and per-row actions:
        </p>
        <ul style={ul}>
          <li><strong>Sync now</strong> — runs the sync immediately and displays a
              detailed summary (created / updated participant counts, attendance rows
              inserted, BC rows fetched, mapped program id, upcoming session count for
              that program).</li>
          <li><strong>Delete</strong> — removes the mapping. Existing imported
              participants are NOT deleted (they keep their program link); only the
              mapping itself goes.</li>
        </ul>

        <h3 style={h3}>One Coaching Program with multiple Reg Groups</h3>
        <p style={para}>
          A typical pattern: one Coaching Program (e.g. "Elite Hawks REP Term 1
          2026") that runs on multiple days, each day a separate BC Registration
          Group. Players who registered for Wednesday should land on Wednesday
          sessions only, not Friday.
        </p>
        <p style={para}>
          <strong>Easy way (recommended):</strong> pick the comp's
          <em> "(all registration groups)"</em> entry in the dropdown, choose
          a program, set <em>Generate sessions from / through</em>. The mapping
          fans out: SwishIQ auto-discovers every Reg Group of that comp, seeds a
          weekly template per Reg Group (using each group's parsed day + time),
          and generates the full term's sessions for each — all under the
          same Coaching Program. Each session is tagged with its source Reg
          Group, so the next sync attaches Wed-Reg-Group people only to Wed
          sessions, Fri-Reg-Group people only to Fri sessions, etc.
        </p>
        <p style={para}>
          <strong>Manual way (per Reg Group):</strong> add one mapping per
          Reg Group instead. Same end result — each mapping seeds its own
          template + sessions. Useful if you only want some Reg Groups in
          SwishIQ, or each Reg Group needs different coaches/locations.
        </p>
        <p style={para}>
          Each generated Program is tagged with the Reg Group's <code>divisionId</code>.
          When the sync runs, Wednesday-registered players are attached only to
          Programs tagged with the Wednesday Reg Group; Friday players only to
          Friday Programs. Manually-created sessions (added via the Coach Planner
          "+ Add Session" button) have no division tag and accept all mappings'
          participants — same as the legacy behaviour.
        </p>

        <h3 style={h3}>Sync rule (what attaches to what)</h3>
        <p style={para}>
          The mapping is the source of truth. Every BC participant whose registration
          matches the mapping's scope (whole competition or specific Reg Group) gets:
        </p>
        <ul style={ul}>
          <li>Created or updated in <code>Participants</code> (smart-merge: existing
              fields like manually-set gender/email/mobile aren't overwritten).</li>
          <li>Linked to the mapped program via <code>ParticipantProgramTypes</code>
              with <code>isFinancial</code> set from BC's payment status
              (<em>Registered</em> = paid, anything else = not paid).</li>
          <li>Attached to upcoming Programs of the mapped program type via
              <code>ProgramParticipantAttendance</code>, scoped by Reg Group:
              participants only land on Programs whose <code>externalDivisionId</code>
              matches the mapping's (or that have NULL — i.e. manually-created
              sessions, which accept everyone). Idempotent — re-running the sync
              won't double-add.</li>
          <li>Recorded in <code>ParticipantBCRegistrationGroups</code> as
              "this person belongs to this Reg Group within this Coaching
              Program". This is what the Coach Planner uses to FILTER each
              session's participant LIST — so a Wednesday-Reg-Group person
              doesn't show up at all on Friday session tiles.</li>
        </ul>
        <p style={para}>
          Whole-competition mappings (no specific Reg Group picked) are unscoped:
          all matching participants attach to all upcoming sessions of the program.
          That's the right shape when one SwishIQ program covers a whole BC comp with
          no day/group split.
        </p>

        <h3 style={h3}>What Coach Planner shows per session</h3>
        <p style={para}>
          Each session tile only lists participants whose Reg Group(s) include
          that session's <code>externalDivisionId</code>. Manually-created
          sessions (no division tag) and manually-added participants (no BC
          Reg Group memberships at all) are shown on every session of the
          program — same as the legacy behaviour, so your existing data
          isn't disturbed.
        </p>
        <p style={para}>
          Click <em>Select Other Participants</em> on a tile to add someone
          who's enrolled in this Coaching Program but in a <em>different</em>
          Registration Group — e.g. a Wednesday-Reg-Group player you want on a
          Friday session for one week only. The picker scopes itself to those
          off-roster people only (everyone already in this session's Reg Group
          is hidden, since they're already on the tile). Same modal works on
          the My Portal coach view for your assigned sessions.
        </p>

        <h3 style={h3}>Recent sync runs</h3>
        <p style={para}>
          The bottom card lists the last 20 sync executions: when each started/finished,
          the BC competitionId, the mapped programTypeId, and how many records were
          created/updated. Errors (if any) are shown in red. Useful for spotting which
          mapping last failed and when.
        </p>
      </div>
    )
  },
  {
    key: 'admin-security-profiles',
    label: 'Security Profiles',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'security-profiles' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Security Profiles</h2>
        <p style={para}>
          Profiles are named bundles of permissions. Examples:
        </p>
        <ul style={ul}>
          <li><strong>Full Admin</strong> — every section, every page, edit access.</li>
          <li><strong>Coach Coordinator</strong> — Coach Planner + Participants + the Coach-related Message tools.</li>
          <li><strong>View Only</strong> — read access to roster + reports, no edits.</li>
        </ul>
        <h3 style={h3}>Permission shape</h3>
        <p style={para}>
          Each row is a (Page, Section, Level) triple. Levels are:
        </p>
        <ul style={ul}>
          <li><strong>edit</strong> — can view AND modify.</li>
          <li><strong>view</strong> — can view but not modify (forms become read-only).</li>
          <li><strong>hide</strong> — page/section is hidden entirely.</li>
        </ul>
        <h3 style={h3}>Composite pages</h3>
        <p style={para}>
          People, Rosters and Message are <em>composite</em> pages — visible if any of their
          sub-permissions are granted. The Help page filters its sidebar the same way.
        </p>
      </div>
    )
  },
  {
    key: 'admin-site-permissions',
    label: 'Site Permissions',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'site-permissions' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Site Permissions</h2>
        <p style={para}>
          Maps each user (by email) to one Security Profile. To grant or revoke access:
        </p>
        <ol style={ol}>
          <li>Find or add the user's email.</li>
          <li>Pick a profile from the dropdown.</li>
          <li>Save.</li>
        </ol>
        <p style={para}>
          Permissions reload on next sign-in. If you change your own profile, sign out and back in
          to see the change.
        </p>
        <div style={warnBox}>
          Don't remove your own Full Admin assignment — you'll lock yourself out and need a database
          fix to recover.
        </div>
      </div>
    )
  },
  {
    key: 'admin-roster-response',
    label: 'Roster Response',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'roster-response' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Roster Response</h2>
        <p style={para}>
          Configures whether staff can accept or reject roster assignments through their My Portal
          page, and how close to the start time they can still change their response.
        </p>
        <p style={para}>
          Each of the three roster categories — <strong>Game</strong>, <strong>Coach</strong>,
          and <strong>Court</strong> — has two independent settings:
        </p>
        <ul style={ul}>
          <li><strong>Enabled</strong> — toggles whether the accept/reject buttons appear at all in My Portal for that category. Off means the assignment is informational only; the person can't respond.</li>
          <li><strong>Cutoff hours</strong> — how many hours <em>before</em> the start time the buttons lock. A coach can accept or reject any time from when they're rostered up until <em>this many hours before the program starts</em>. After the cutoff, the response is final.</li>
        </ul>

        <h3 style={h3}>Default: 24 hours</h3>
        <p style={para}>
          Out of the box every category is <strong>Enabled</strong> with a <strong>24-hour</strong>
          cutoff. So a coach assigned to a Wednesday 7pm program can accept or reject any time
          from when they receive the roster SMS until Tuesday 7pm — after that the buttons in My
          Portal grey out. The same applies to game and court assignments.
        </p>

        <h3 style={h3}>Choosing a value</h3>
        <ul style={ul}>
          <li><strong>Larger cutoff (e.g. 48-72 hours)</strong> — gives admins more time to find replacements after a rejection.</li>
          <li><strong>Smaller cutoff (e.g. 2-12 hours)</strong> — lets staff still drop late, useful for last-minute swaps.</li>
          <li><strong>0 hours</strong> — staff can change their response right up to the start time.</li>
          <li><strong>Disabled</strong> — no accept/reject at all; assignments are commitments by default.</li>
        </ul>

        <div style={tipBox}>
          The setting takes effect immediately — there's no reload or batch job. Changes are stored
          as <code style={code}>ROSTER_CUTOFF_GAME</code>, <code style={code}>ROSTER_CUTOFF_COACH</code>,
          and <code style={code}>ROSTER_CUTOFF_COURT</code> in the Settings table.
        </div>
        <div style={noteBox}>
          The cutoff doesn't lock attendance — a coach who accepted then no-shows is still tracked
          via the per-program <em>attended</em> tick on the Coach Planner.
        </div>
      </div>
    )
  },
  {
    key: 'admin-business-banking',
    label: 'Business Banking',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'business-banking' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Business Banking</h2>
        <p style={para}>
          Your association's bank account details, used as the source account in the ABA file
          generated by Payroll. Every field is required by the ABA spec:
        </p>
        <ul style={ul}>
          <li><strong>BSB</strong> + <strong>Account Number</strong> + <strong>Account Name</strong></li>
          <li><strong>Financial Institution Code</strong> — three-letter bank code (e.g. CBA, NAB).</li>
          <li><strong>Lodgement Reference</strong> — what shows on payee statements.</li>
          <li><strong>Name of User Supplying File</strong> + <strong>APCA Number</strong> — issued by your bank when they enable batch payments.</li>
        </ul>
      </div>
    )
  },
  {
    key: 'admin-sms',
    label: 'SMS Configuration',
    group: 'Admin',
    requires: { pageKey: 'admin', sectionKey: 'sms' },
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → SMS</h2>
        <p style={para}>
          Stores Mobile Message API credentials and lets you send a test SMS. All bulk and
          per-person sends live on the top-level <strong>Message</strong> tab.
        </p>
        <h3 style={h3}>Initial setup</h3>
        <ol style={ol}>
          <li>Get an API username + password from your Mobile Message account settings.</li>
          <li>Register a Sender ID (alphanumeric like "Snakepit" or your verified mobile number) on the Mobile Message dashboard.</li>
          <li>Enter all three here, tick <strong>Enabled</strong>, click <strong>Save</strong>.</li>
          <li>Click <strong>Check Balance</strong> — if a number comes back, credentials work.</li>
          <li>Use <strong>Send Test SMS</strong> with your own phone to verify end-to-end.</li>
        </ol>
        <h3 style={h3}>Low-balance threshold</h3>
        <p style={para}>
          When the post-send balance drops below this number, a popup warns to top up credits ASAP.
          Default is 50 — adjust to match your typical send volume.
        </p>
        <h3 style={h3}>Compliance built in</h3>
        <p style={para}>
          Every outbound message has <code style={code}>{'{optout}'}</code> appended automatically;
          Mobile Message expands it to legally compliant unsubscribe text. Anyone who replies STOP
          is auto-blocked from future sends without you having to manage the list.
        </p>
      </div>
    )
  },
  {
    key: 'admin-qualifications',
    label: 'Qualifications',
    group: 'Admin',
    requires: [
      { pageKey: 'admin', sectionKey: 'referee-qualifications' },
      { pageKey: 'admin', sectionKey: 'bench-qualifications' },
      { pageKey: 'admin', sectionKey: 'court-qualifications' },
      { pageKey: 'admin', sectionKey: 'coach-qualifications' },
      { pageKey: 'admin', sectionKey: 'division-age-groups' }
    ],
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Admin → Qualifications</h2>
        <p style={para}>
          Qualification records define pay rates and eligibility for each role:
        </p>
        <h3 style={h3}>Referee + Bench Official qualifications</h3>
        <p style={para}>
          Each qualification has a Level (e.g. Association Community, State Development) and a pay
          rate per <strong>age band</strong>. The pay schema was simplified (16 May 2026) from 28
          per-division fields to <strong>6 referee bands</strong> — U10/U12/U14, U16-18 Div 2&3,
          U16-18 Div 1/Championship, Seniors Div 1-5, Championship Seniors, Social — and{' '}
          <strong>2 bench bands</strong> (Junior games, Senior games), matching the association
          pay matrix.
        </p>
        <div style={noteBox}>
          <strong>Blank = not assignable.</strong> Leave a band blank (not 0) and the person can't
          be assigned to games in that band — AI Allocate and the Game Planner dropdowns exclude
          them, and the slot would resolve to $0. This is the intended way to scope a qualification
          (e.g. a Trainee qual has no Seniors/Championship rate, so trainees never get senior games
          from the allocator).
        </div>
        <h3 style={h3}>Court Official qualifications</h3>
        <p style={para}>
          Hourly pay rate (one number, not per-age-group like refs).
        </p>
        <h3 style={h3}>Coach qualifications</h3>
        <p style={para}>
          Per-hour pay used by the Coach Planner.
        </p>
        <h3 style={h3}>Division Age-Group mappings</h3>
        <p style={para}>
          Bridges the team-level division name (from the upstream sport API) to the age-group key
          used by qualification pay-rate columns. Without this mapping the planner falls back to
          "Social Grade 1" for any unmapped division.
        </p>
        <h3 style={h3}>Per-person qualifications</h3>
        <p style={para}>
          Assign which qualifications each Person holds in <strong>People → Person → Edit</strong>.
          A person can hold multiple qualifications across categories (e.g. Level 4 referee +
          Level 2 bench).
        </p>
      </div>
    )
  },

  // ============ My Portal (always visible — useful for admins helping users) ============
  {
    key: 'my-portal',
    label: 'My Portal (for staff & parents)',
    group: 'Reference',
    requires: null,
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>My Portal</h2>
        <p style={para}>
          The mobile-first public site that staff and parents reach via a personal availability
          link (URL containing <code style={code}>#availability=&lt;token&gt;</code>). They never
          sign in to the main app.
        </p>
        <h3 style={h3}>Sharing the link</h3>
        <ul style={ul}>
          <li><strong>Email</strong> — People page, per-row Email Link button.</li>
          <li><strong>SMS</strong> — Message tab → SMS one or more people their roster (the message includes the link).</li>
          <li><strong>Copy</strong> — People page, per-row Copy Link button (paste into your own message channel).</li>
        </ul>
        <h3 style={h3}>What's on the portal</h3>
        <ul style={ul}>
          <li><strong>Summary</strong> — all this person's assignments for the week (games + coaching + court).</li>
          <li><strong>Game</strong> / <strong>Coach</strong> / <strong>Court</strong> — the full schedule for context.</li>
          <li><strong>Availability</strong> — set their weekly availability windows. Saved instantly.</li>
          <li><strong>Programs</strong> — allocate a new participant to a coaching program. The signup URL is SMS'd to the contact mobile entered on the form so they can complete registration.</li>
        </ul>

        <h3 style={h3}>Accepting / rejecting shifts</h3>
        <p style={para}>
          Each assignment row has Accept and Reject buttons until a configurable cutoff. The default
          window is <strong>24 hours before the shift starts</strong> for games, coaching programs,
          and court assignments. After that the buttons grey out and the response is final. Admins
          can change the cutoff (or hide the buttons entirely) per category in
          <strong> Admin → Roster Response</strong>.
        </p>
        <h3 style={h3}>SMS verification (mandatory)</h3>
        <p style={para}>
          Every visit to My Portal on a new browser is gated by a one-time SMS code. The system
          sends a 6-digit code to the person's mobile (the number stored in
          <strong> People → Mobile</strong>); they enter it on the verification screen to unlock
          the portal. The browser stays unlocked for <strong>30 days</strong> via a secure signed
          cookie — they won't be asked again on that device for 30 days.
        </p>
        <ul style={ul}>
          <li><strong>Codes expire after 10 minutes</strong> and become unusable.</li>
          <li><strong>Resend is throttled to once per 60 seconds</strong> to prevent SMS-bombing.</li>
          <li><strong>5 wrong attempts</strong> burns the code — they have to request a fresh one.</li>
          <li><strong>No mobile on file</strong> = the portal cannot be unlocked. Add a mobile to the person's record under People to enable verification.</li>
          <li>Admins can clear an existing person's MFA state via <strong>People → Reset MFA</strong>; the person will be prompted for SMS verification again on their next visit. To force a re-verification on a device that's already inside the 30-day window, rotate the person's availability token under <strong>People → Generate Availability Token</strong>.</li>
        </ul>
        <h3 style={h3}>Tokens are sensitive</h3>
        <p style={para}>
          A person's availability link IS their identity to the portal — anyone who has the URL has
          their access. Generate a new token from <strong>People → Generate Availability
          Token</strong> if a link is leaked.
        </p>
      </div>
    )
  },
  {
    key: 'support',
    label: 'Support & Glossary',
    group: 'Reference',
    requires: null,
    render: () => (
      <div style={sectionWrap}>
        <h2 style={h2}>Support &amp; Glossary</h2>
        <h3 style={h3}>Glossary</h3>
        <ul style={ul}>
          <li><strong>Game Assignment Slot</strong> — one row representing one person assigned to one role on one game (referee or bench official). A game can have many slots.</li>
          <li><strong>Program Type</strong> — long-running coaching enrolment (e.g. "U10 Skills Term 2"). Has start/end dates.</li>
          <li><strong>Program</strong> — a single session of a Program Type at a specific date/time.</li>
          <li><strong>Participant</strong> — an athlete enrolled in one or more Program Types.</li>
          <li><strong>ProgramParticipantAttendance</strong> — selection + attendance for a Participant in a specific Program (session).</li>
          <li><strong>ParticipantProgramTypes</strong> — long-term enrolment link, with the per-program <em>isFinancial</em> flag.</li>
          <li><strong>Forecast pay</strong> — pay for an assignment as scheduled (whether or not attended).</li>
          <li><strong>Actual pay</strong> — pay for assignments where the person was marked attended.</li>
          <li><strong>ABA file</strong> — Australian banking format for batch payments. Generated by Payroll.</li>
          <li><strong>MFA</strong> — Multi-Factor Authentication via TOTP (e.g. Google Authenticator, Authy). Required for the My Portal.</li>
        </ul>
        <h3 style={h3}>Common gotchas</h3>
        <ul style={ul}>
          <li><strong>"Why isn't person X showing in the dropdown?"</strong> — They lack the required qualification for that division (pay rate is 0), they're not available at that time, they're playing in the same timeslot, or they're already assigned somewhere else at that time.</li>
          <li><strong>"The team name shows as Team 12345"</strong> — The upstream API didn't return that team. Run <em>Admin → Settings → Sync Teams from Upstream</em> with a recent date.</li>
          <li><strong>"My SMS test failed"</strong> — Confirm SMS is Enabled in Admin → SMS, then click Check Balance. If balance returns a number, your config is correct; if not, the API username/password is wrong.</li>
        </ul>
        <h3 style={h3}>Reporting an issue</h3>
        <p style={para}>
          Capture: the page URL, what you clicked, what you expected, and what happened (screenshot
          if visual). Check <strong>Admin → Audit</strong> with a category filter for the relevant
          area to see if the action even reached the backend.
        </p>
      </div>
    )
  },

  // ============ Release Notes ============
  // Always visible to every user. Newest entries at the top. When shipping
  // a release, add a new dated block here so the log is built into the app
  // rather than living in a separate document.
  {
    key: 'release-notes',
    label: 'Release Notes',
    group: 'Release Notes',
    requires: null,
    render: () => {
      const releaseHeading = { color: '#A4161A', marginTop: '1.4rem', marginBottom: '0.4rem', borderBottom: '1px solid #f4cdd0', paddingBottom: 4 };
      const dateBadge = { display: 'inline-block', background: '#A4161A', color: 'white', fontSize: '0.75rem', padding: '2px 8px', borderRadius: 12, fontWeight: 600, marginRight: 8 };
      return (
        <div style={sectionWrap}>
          <h2 style={h2}>Release Notes</h2>
          <p style={para}>
            A running log of changes shipped to roster.snakepit.com.au. Newest at the top. Items are
            grouped by the date the release went live; smaller patch deploys are folded into the
            nearest dated block.
          </p>

          {RELEASE_NOTES(releaseHeading, dateBadge)}

          <h3 style={releaseHeading}><span style={dateBadge}>16 May 2026</span>Configurable Referee Codes, "Home" rename, scores on Home, AI Allocate help, layout fixes</h3>
          <ul style={ul}>
            <li><strong>"Rosters" tab renamed to "Home".</strong> Same page, just a friendlier label — it's the default landing page so "Home" matches what you actually use it for. Underlying permission key (<code style={code}>rosters / rosters</code>) is unchanged so no security-profile rebuild is needed.</li>
            <li><strong>Final scores + winning team on the Home page.</strong> Every layout (by-person, by-court, by-day, Up Next) now renders the matchup as <em>TeamA (score) vs TeamB (score)</em> with the winner in <strong>bold</strong> once both scores are imported. Same formatting as the Game Planner, so the result is obvious wherever you look. Tied games and games still missing a score render at the default weight.</li>
            <li><strong>Game Planner: winning team in bold</strong> — same treatment on the per-game tile. Ties / missing scores → no emphasis.</li>
            <li><strong>Budget Bar no longer disappears on click.</strong> The Daily Cost / Budget bar is now permanently mounted on the Game Planner — values update in place during a recalc instead of the whole bar disappearing for ~200ms and the page jumping up and back down. Also added a CSS <code style={code}>min-height</code> safety net so the layout box can't collapse even transiently.</li>
            <li><strong>Empty court columns dropped from the Game Planner grid.</strong> A court with no games on the selected day (e.g. Beaton on a midweek night) is removed from the column layout entirely, and the remaining 4 courts widen to fill the freed space. Same fix on the Home page Court and Day views.</li>
            <li><strong>Wider page layout.</strong> Main-content max-width bumped from 1200px → 1700px so referee + bench dropdowns have room to show full names ("Maeve Whitten - Junior Bench Official") instead of truncating to "Maeve W...".</li>
            <li><strong>"All Attended" button on Game Planner.</strong> One-click bulk action to mark every referee + bench official assignment on the selected day as attended. Only flips slots that aren't already attended; nothing is reset. Visible to users with edit permission.</li>
            <li><strong>Permission gating for admin actions.</strong> On Game Planner, the <em>Lock Day</em> button is now hidden (not just disabled) for users with view-only permission, matching the existing behaviour of <em>AI Allocate</em>, <em>Import Roster</em>, and <em>All Attended</em>. Coach Planner <em>Add Session</em> + <em>Lock Day</em> and Court Planner <em>Add Court Officials</em> + <em>Lock Day</em> were already gated.</li>
            <li><strong>Coach Planner Participants button restyled.</strong> The shortcut to People → Participants is now the same outline-red pill style as Add Session and Lock Day instead of solid blue — visually consistent with its neighbours.</li>
            <li><strong>AI Allocate: Help expanded.</strong> The Game help section now documents the algorithm in detail — what gets skipped, the rejection filters (availability / playing / already-in-slot / qualification), the scoring weights (100 base + affinity max 30 + tier match 5/15/25 + fairness penalty −15 per game above average), and how the preview / Apply step works.</li>
            <li><strong>New admin tab: Referee Codes.</strong> Admin → Referee Codes is the configuration screen for the small chip that prefixes each referee slot in the Game Planner. Defaults seeded as <em>CC</em> (Crew Chief), <em>R</em> (Referee), <em>T</em> (Trainee). You can add codes (e.g. <em>M</em> for Mentor), rename labels, change order, or archive any of them. Edits take effect on the next page refresh.</li>
            <li><strong>Game Planner: chip is now click-to-cycle.</strong> The chip beside each referee dropdown is now a small clickable button. Click <em>CC</em> → it becomes <em>R</em>; click again → <em>T</em>; again → wraps back to <em>CC</em> (or whatever cycle order you've configured). The choice is persisted on the per-slot level — so two games can each have their referees in different orders. The previous hard-coded "slot 1 = CC, slot 2 = R, slot 3 = T" mapping is preserved as the default for newly-added slots, so existing UX is unchanged out of the box.</li>
            <li><strong>My Portal + Roster View: read-only chip.</strong> The same code appears beside the referee's name in My Portal (Game tab + Summary) and in the desktop Roster View (Day, Court, and Up Next tiles), but it can't be edited from there — only from the Game Planner.</li>
            <li><strong>New permission key:</strong> <code style={code}>admin / referee-codes</code>. Existing security profiles default to hide on new keys; on this release any profile that already had <em>edit</em> on <code style={code}>admin / audit</code> automatically inherits <em>edit</em> on the new key. Other profiles need explicit grant via Admin → Security Profiles.</li>
            <li><strong>Backfill of existing rosters.</strong> Every existing referee assignment was tagged with the code matching its slot order on first boot, so no historical roster row appears blank.</li>
            <li><strong>Per-role Active flags on every Person.</strong> Four new boolean columns on Person — <code style={code}>isActiveReferee</code>, <code style={code}>isActiveBench</code>, <code style={code}>isActiveCoach</code>, <code style={code}>isActiveCourt</code> — gate each function independently of the global <em>isActive</em>. A person needs both a relevant qualification AND the matching Active flag to appear in the corresponding dropdown (Game Planner ref/bench, Coach Planner coach, Court Planner court official) and to be considered by AI Allocate. Inactive roles keep historical assignments + qualifications — the person just stops appearing in pickers.</li>
            <li><strong>One-time 14-day backfill.</strong> On first deploy, anyone rostered to a function in the last 14 days has the matching flag flipped on automatically (e.g. anyone reffed in the last fortnight gets <em>isActiveReferee</em>). Per-role counts are logged on boot. After the backfill the flag is purely manual — new people start inactive.</li>
            <li><strong>People &rarr; Edit Person form: Active Status section.</strong> Four checkboxes (Active Referee / Bench Official / Coach / Court Official) at the bottom of the form, with a one-line explainer.</li>
            <li><strong>People &rarr; new Referees / Bench / Coach / Court sub-tabs.</strong> Each shows the current active list (name, qualifications, contact) with a Remove button + an <em>+ Add Active …</em> button. The Add modal lists every person with the relevant qualification who isn't yet active; multi-select and one click marks them all active in a single bulk write. Each sub-tab is its own permission section so an admin can grant access to (e.g.) just Active Coaches without exposing the others.</li>
            <li><strong>AI Allocate respects the new flags.</strong> The referee + bench candidate queries inside <code style={code}>POST /api/games/auto-allocate</code> now also require the corresponding Active flag, so a person who isn't an active referee can't be allocated even if they hold a referee qualification.</li>
            <li><strong>New endpoints:</strong> <code style={code}>GET /api/person/active/:role</code>, <code style={code}>GET /api/person/candidates/:role</code>, <code style={code}>POST /api/person/active/:role/bulk</code>, <code style={code}>DELETE /api/person/active/:role/:personId</code> — each <code style={code}>:role</code> is one of <em>referee</em>, <em>bench</em>, <em>coach</em>, <em>court</em>. The PUT <code style={code}>/api/person/:id</code> body now also accepts the four new fields (omitted = preserved).</li>
            <li><strong>New permission keys:</strong> <code style={code}>admin / active-referees</code>, <code style={code}>admin / active-bench</code>, <code style={code}>admin / active-coaches</code>, <code style={code}>admin / active-court</code>. New keys default to <em>hide</em>; grant via Admin &rarr; Security Profiles.</li>
            <li><strong>Game / Coach / Court Planner: dropdown N+1 fix.</strong> The referee, bench-official, and court-official dropdown loads were doing one HTTP request per person to fetch their qualifications, serially — 5–10 seconds on a 50-person roster. <code style={code}>/api/referees</code> and <code style={code}>/api/court-officials</code> now return one row per (person × qualification) with the pay columns inlined (matching the existing <code style={code}>/api/bench-officials</code> shape), so the frontend builds dropdown options in one round-trip. <code style={code}>preloadAvailability</code> on the Game Planner also switched from serial <code style={code}>for/await</code> to <code style={code}>Promise.all</code>, so per-person availability fetches run in parallel — first paint dropped from ~10s to ~300ms on the same data.</li>
            <li><strong>AI Allocate: Championship games require a senior referee.</strong> A new hard rule — every Championship-division game must have ≥1 assigned referee holding a senior tier (Association Advanced / Veterans 1, State Development, State Grading / Veterans 2, or National Development / National). The allocator promotes the best available senior over the weakest non-senior pick if the natural top-3 lacks one; if none are eligible the game is flagged in "Couldn't make". The match pattern is tunable in Admin → AI Management (<code style={code}>championshipSeniorRegex</code>, default <code style={code}>advanced|veteran|national|state</code>).</li>
            <li><strong>AI Allocate: rule set v2 (full rewrite per operator spec).</strong> All previous scoring rules removed (stored configs predating v2 are discarded). New hard constraints: Championship divisions get 3 referees + 3 bench (others 2 + 1); a person whose assigned team is playing is excluded; nobody can be on bench while a referee in the same slot (or vice-versa); trainees are never auto-assigned (manual placement). Processing order is fixed — Championship games first, then youngest → oldest so the cheapest referees land on the youngest divisions. New soft factors: cost-per-dollar, historical-division match, same-day clustering, prior-calendar-month fairness average, and a lowest-weight female-referee → girls/women's-game nudge. New <code style={code}>Person.gender</code> column + a Gender select on the People edit form feed that last factor (unset = ignored).</li>
            <li><strong>Pay schema simplified: 28 per-division fields → 6 referee + 2 bench bands.</strong> RefereeQualifications now has <code style={code}>payJuniors</code> (U10/U12/U14), <code style={code}>payYouthDev</code> (U16-18 Div 2&3), <code style={code}>payYouthChamp</code> (U16-18 Div 1/Championship), <code style={code}>paySeniors</code> (Seniors Div 1-5), <code style={code}>payChampSeniors</code> (Championship Seniors), <code style={code}>paySocial</code>. BenchOfficialQualifications has <code style={code}>payJuniorGames</code> + <code style={code}>paySeniorGames</code>. A one-time migration seeds every existing qualification's bands from the association pay matrix (matched by level name) then drops the old 28 columns from both tables. The ~9 scattered age-group→column maps across the backend were replaced with a single canonical resolver (refBandForAgeGroup / benchBandForAgeGroup) so pay logic has one source of truth. <strong>A blank band means the person can't be assigned to games in that band</strong> — the Qualifications form now shows 6/2 inputs with a "blank = not assignable" hint, and AI Allocate honours it.</li>
            <li><strong>AI Allocate: tuned for cheaper rosters by default.</strong> Two new tunables shipped via Admin → AI Management. <em>Cost Penalty Per Dollar</em> (new scoring rule, default 1.0) subtracts (per-game pay × weight) from the candidate's score so cheaper refs beat expensive ones when tier match + affinity are otherwise close. <em>Process Lowest Age Groups First</em> (new limit toggle, default on) flips the game-processing order so cheap refs grab the cheap-tier games before being swept onto more expensive ones. Default Division Affinity weight halved from 30 → 15 so cost dominates "they always ref this division" preference. The loader merges new scoring rules into existing saved configs so operators with custom tweaks get the new rule at its default weight without losing their other edits.</li>
            <li><strong>New admin tab: AI Management.</strong> Tunes the AI Allocate algorithm without a code change. Six scoring rules (each with weight + enabled toggle + up/down reorder) and eight limits (max refs/bench per game, conflict window, affinity lookback, person-tier thresholds, trainee exclusion + regex). Saves to a single <code style={code}>AI_ALLOCATE_CONFIG_v1</code> row in Settings; the next AI Allocate run picks up the new values automatically. Reset wipes the row so the loader returns the baked-in defaults. Audit-logged on every save. New permission key <code style={code}>admin / ai-management</code> defaults to hide; grant via Admin → Security Profiles.</li>
            <li><strong>AI Allocate: zero-pay slot diagnostic.</strong> The Last Period cost cell now annotates "⚠ N $0 slots" when last week has slots assigned to someone whose qualification on file pays $0 for the game's division. The per-day breakdown gets a new <em>$0 Slots</em> column showing the same per-day. When any zero-pay slots are present, an inline callout below the table explains that those slots are the most common reason AI Allocate looks more expensive than last week — they suppress last week's actual cost while AI fills every slot at a real non-zero rate. Two suggested fixes (check Admin → Qualifications for missing rates; verify the right qualification is selected on the Game Planner dropdown) are spelled out.</li>
            <li><strong>AI Allocate: cap at 2 referees per game; trainees excluded.</strong> The allocator now fills only Crew Chief + Referee per game (was up to 3 including the Trainee slot). Trainee qualifications (any RefereeQualifications level whose name contains "Trainee", case-insensitive) are also stripped from the candidate pool — a senior ref who happens to also hold a Trainee qual still gets considered at their senior rate, but a trainee-only person is never proposed. Trainee placement is a manual coaching call now made via the Game Planner dropdowns. Materially reduces the proposed cost (no more 3rd-ref slot) and removes a likely cause of "AI is twice as expensive as last week" reports.</li>
            <li><strong>Game Planner: Clear Roster button.</strong> New red button next to <em>All Attended</em>. Wipes every referee + bench official assignment for the selected day (including any already marked attended) in one shot. Two confirms before it fires; locked days are refused with a 400 + clear message. Audit log captures the slot counts (refs / bench / previously-attended / games affected) for traceability. Backed by <code style={code}>POST /api/games/by-day/:date/clear-roster</code> (admin-only).</li>
            <li><strong>AI Allocate: side-by-side per-day breakdown vs same day last week.</strong> Collapsible diagnostic table inside the modal. Each row pairs a proposed day with the same day-of-week from the previous week (Mon ↔ Mon) so the comparison is apples-to-apples. Δ Cost and Δ Slots columns highlight which day(s) drive a delta and whether it's a volume issue or a rate issue. Lets operators investigate "why is the proposed cost so different from last week" without exporting to a spreadsheet.</li>
            <li><strong>AI Allocate: cost-comparison detail + CSV / PDF export.</strong> The Last Period and Proposed cells in the cost-comparison strip now show the underlying <em>game count, slot count, and $/slot rate</em> — so a big period-over-period delta is easy to diagnose as either a volume issue (different slot count) or a rate issue (more expensive qualifications). Each result tab also has Export CSV + Print &amp; PDF buttons that emit the visible table (Per Game or Per Person) with the cost summary at the top of the printed view.</li>
            <li><strong>AI Allocate: cost-comparison strip + Per Game tab.</strong> The result modal now opens with three side-by-side numbers — <em>Last Period</em> (actual pay across the same-length window immediately before the proposed range), <em>Budget</em> (sum of Daily Budgets across the proposed range), and <em>Proposed</em> (this run's total cost; goes red when it exceeds Budget). A new <em>Per Game</em> tab next to the existing <em>Per Person</em> table lists every game with its proposed Crew Chief / Referee / Trainee / Bench so you can sanity-check assignments before clicking Apply. Skipped games (locked / attended) are flagged inline.</li>
            <li><strong>Active requires a qualification (enforced).</strong> Followup polish on the per-role Active flags. The Person edit form now greys out an Active checkbox (with a "no qualification" hint) when the person holds no qualification of that type. Removing a person's last qualification of a given type also auto-unticks the matching Active flag so the form can't desync. The backend <code style={code}>PUT /api/person/:id</code> rejects with a 400 + clear message if a request tries to set an Active flag without a corresponding qualification, and the bulk endpoint <code style={code}>POST /api/person/active/:role/bulk</code> silently filters out unqualified IDs (response now includes <code style={code}>updated</code>, <code style={code}>requested</code>, and <code style={code}>skipped</code> counts).</li>
          </ul>

          <h3 style={releaseHeading}><span style={dateBadge}>15 May 2026</span>Mandatory SMS-OTP gate on My Portal</h3>
          <ul style={ul}>
            <li><strong>My Portal is now strictly gated by SMS one-time-password.</strong> Opening the portal on a new browser sends a 6-digit code to the person's mobile (the value stored on their People record). They enter it on the verification screen to unlock the portal. No code = no access — there's no longer an "MFA is optional until you set it up" path.</li>
            <li><strong>Browser stays unlocked for 30 days.</strong> Successful verification sets a signed, secure, HTTP-only cookie scoped to that person; subsequent visits within 30 days skip the SMS step. Expires after 30 days, then SMS is requested again.</li>
            <li><strong>Codes expire 10 minutes after they're sent.</strong> Resend is throttled to once per 60 seconds (server-enforced) so the SMS endpoint can't be used to spam someone's phone.</li>
            <li><strong>5 wrong attempts burns the code.</strong> The user is told how many tries remain on each wrong submit; on the 5th wrong guess the code is invalidated and they have to request a new one. There's no permanent lockout — they can always try again with a fresh code.</li>
            <li><strong>Codes are hashed at rest.</strong> Stored as a SHA-256 digest with constant-time compare on verify. The plaintext only exists long enough to send the SMS.</li>
            <li><strong>Security email on every successful unlock.</strong> The person gets an email after a successful SMS verification with the masked mobile and a "if this wasn't you, contact your administrator" prompt. Best-effort; doesn't block access if SMTP is down.</li>
            <li><strong>People → Reset MFA</strong> behaviour updated. It now wipes any active SMS code AND clears the verified flag so the next portal visit gates on SMS again. To also force an already-signed-in browser to re-verify, rotate the availability token via <em>People → Generate Availability Token</em>.</li>
            <li><strong>No mobile on file = clear error message.</strong> If a person's record has no usable Australian mobile, the verification screen explains they need to contact an admin to add one — instead of silently failing.</li>
            <li><strong>TOTP / authenticator-app flow has been removed.</strong> Existing PersonMfa rows are kept for history but the TOTP secret is no longer consulted. Anyone who previously had MFA "set up" with an authenticator app will be asked for an SMS code instead on their next visit.</li>
          </ul>

          <h3 style={releaseHeading}><span style={dateBadge}>14 May 2026</span>Basketball Connect integration, Comp Mapping, Error Log + UX polish</h3>
          <ul style={ul}>
            <li><strong>Admin → BC Integration</strong> — auto-login to Basketball Connect (Squadi). Stores admin credentials encrypted at rest with AES-256-GCM, runs a 2FA login on demand, and re-logins automatically when the session token expires. Test Connection button verifies auth + reports the BC competition count, sample competition, and any upstream error detail.</li>
            <li><strong>New admin tab: Comp Mapping</strong> — pair every BC competition (or specific Registration Group) with a SwishIQ Coaching Program. Live BC competition list (auto-discovered via the registration data, not just the fee listing — covers Reg Groups that listing skips). Per-mapping <em>Sync now</em> + scheduled syncs at <strong>5am, 9am, 12pm, 3pm AEDT</strong>. Recent sync runs panel shows the audit trail.</li>
            <li><strong>"+ Create new program" inline on Comp Mapping</strong> — adds the SwishIQ Coaching Program from the BC name in one shot (case-insensitive dedupe against existing programs). Inline form for Program name, Weekly Budget ($), Signup URL, Location, Default coaches, and a date range (<em>Generate sessions from / through</em>). Save now also auto-fires the first sync so participants land immediately.</li>
            <li><strong>"(all registration groups)" fan-out</strong> — picking that option in the dropdown auto-creates one weekly template + sessions per Reg Group of the comp (each tagged with its own divisionId) so the same Coaching Program can hold e.g. Mon 6am, Wed 6am, Fri 6am sessions. Shared coaches, single date range. Reg Groups at the same physical slot get separate Programs (different age groups, different coaches) — they don't share a tile.</li>
            <li><strong>Per-Reg-Group session filtering.</strong> New <code>ParticipantBCRegistrationGroups</code> table records which Reg Group each BC participant belongs to, and <code>ProgramRegistrationGroups</code> tags each session with its source Reg Group. Coach Planner tiles only list participants whose Reg Group includes that session's tag — Wednesday-registered players stop showing on Friday sessions.</li>
            <li><strong>"Select Other Participants" rescoped</strong> — picker now shows ONLY enrolled participants who AREN'T in the session's Reg Group, i.e. genuine off-roster adds. Same flow on Coach Planner desktop and My Portal. Empty state ("Everyone enrolled is already in this session's Reg Group") when nothing's off-roster. Already-added off-roster extras stay visible so you can untick to remove.</li>
            <li><strong>People → Participants: Import CSV button removed.</strong> CSV-based participant ingestion is deprecated; the BC Integration sync runs automatically. <em>+ Add Participant</em> stays for manual single-entry adds. Backend endpoint <code>/api/participants/import</code> kept available for emergency use.</li>
            <li><strong>Admin → Weekly Templates tab + Coach Planner Import Template button removed.</strong> Both deprecated by the BC Integration mapping flow which seeds the underlying templates + sessions automatically. Backend endpoints stay so existing data isn't disturbed.</li>
            <li><strong>Day-of-week next to every date selector.</strong> Coach Planner, Game Planner (incl. Auto-Allocate range), Roster View week selector, Reports (forecast / participant / session date ranges), Payroll, Message (roster + bulk SMS dates) all show "Wednesday 14 May 2026" alongside the picker so you don't second-guess weekday on a YYYY-MM-DD value.</li>
            <li><strong>New Admin tab: Error Log.</strong> Every server error is captured with full stack, source route + method, request body preview (~2KB), and the user identity (audit headers / JWT). Filter open / resolved / all; click a row for the detail panel; resolve with an inline note. Captured from both <code>sendServerError()</code> calls and a global Express error middleware (anything that escapes a route handler's try/catch).</li>
            <li><strong>Coach Planner tile cleanup.</strong> Tile no longer clips the <em>Select Other Participants</em> button when content overflows. The "▸ Participants (X/Y)" toggle is now a prominent solid-red button, ~2× the visual weight of <em>Select Other Participants</em>. Coach assignment dropdowns are bigger and easier to tap on touch.</li>
            <li><strong>Admin → Coaching Programs Edit form simplified.</strong> Editing an existing program no longer shows the Weekly Schedule builder or Linked / Feeder Programs picker — both stay in the Add form for new programs. Tweak per-session schedules via the Coach Planner tile or BC Comp Mapping flow instead.</li>
            <li><strong>Help expanded.</strong> New "Common Workflows" Getting Started section with end-to-end recipes (allocate weekend, mark attendance, run payroll, BC mapping fan-out, off-roster add, error triage). New <em>Admin → BC Integration</em>, <em>Admin → Comp Mapping</em>, and <em>Admin → Error Log</em> Help sections.</li>
            <li><strong>Permissions backfill.</strong> Two new permission keys — <code style={code}>admin / competition-mapping</code> and <code style={code}>admin / error-log</code> — were added for the new admin tabs. Existing security profiles default to <em>hide</em> on new keys; on this release, any profile that already had <em>edit</em> on <code style={code}>admin / audit</code> automatically inherits <em>edit</em> on the two new keys. Other profiles need explicit grant via Admin → Security Profiles.</li>
          </ul>

          <h3 style={releaseHeading}><span style={dateBadge}>11 May 2026</span>Multi-owner Coaching Programs &amp; coach-decline alerts</h3>
          <ul style={ul}>
            <li><strong>Coach Planner: light-orange "seen but no response" pill.</strong> The coach's pill on each tile now goes light orange once they've viewed their My Portal coach roster but haven't yet accepted or rejected. Hover for the timestamp of when they first opened it. Lets you see at a glance who's been silent because they haven't looked vs who's been silent after seeing the shift.</li>
            <li><strong>New report: Session Participation.</strong> Per-session detail across any date range (filterable by program type) — Allocated / Attended counts, financial breakdown of attendees (Paid / Not paid / Unknown) and source breakdown (Targeted to this session / Enrolled but in another session / Global = no targets). Click <em>Details</em> for the per-participant drill-down. PDF and Excel exports include the drill-down rows for traceability.</li>
            <li><strong>Editing a Coaching Program now syncs future sessions.</strong> When you change days/times/duration/courts or add default coaches and save, every upcoming session for that program is brought into line automatically: matching sessions get their duration + default coaches updated; sessions whose schedule row was removed are safely deleted (only when not yet attended); sessions with attendance are preserved untouched; and any newly-added schedule rows are materialized for future weekdays. Past sessions are never touched. The success message after saving shows the per-action counts.</li>
            <li><strong>My Portal Coach: Select Other Participants button.</strong> The same flow that exists on the desktop Coach Planner is now on every session card in My Portal. Tap the button to open a list of every participant enrolled in the program (or any feeder program) and tick anyone you want to add to today's session — handy for guest-attendees who normally come on a different day. Untick is allowed only if the participant hasn't already been marked attended (so you can't lose attendance history). The desktop button has been renamed from <em>Select Participants</em> to <em>Select Other Participants</em> to match.</li>
            <li><strong>Coaching Program schedule: default coaches per session row.</strong> Each row in the Weekly Schedule builder now has a <em>Default coaches</em> picker — up to 5 coaches per row, each as a (person + qualification level) pair. Sessions materialized from the schedule (or the Copy Day / Import Template flows that share the same logic) get those coaches auto-allocated at the right qualification level — same rate as if you'd picked them manually on the Coach Planner.</li>
            <li><strong>Coach Planner: date navigation now spans &plusmn;365 days</strong> (was &plusmn;21). Lets you scroll through a full term/year-long Coaching Program without hitting a hard stop. The Copy Day target-date picker uses the same range.</li>
            <li><strong>Rosters &rarr; Day view: Up Next banner + past days rolled up.</strong> The same Up Next strip from the Court view now appears at the top of the Day view too. Past days within the current week are tucked behind a <em>Show past day(s) this week (N)</em> toggle so today and future days dominate the screen.</li>
            <li><strong>Coaching Program form: Weekly Schedule builder.</strong> Define each weekly recurring session as one row (day-of-week, start time, duration, court). Multiple rows allowed — e.g. <em>Mon 6am 120min Court 1</em>, <em>Mon 7am 120min Court 2</em>, <em>Thu 6am 120min Court 1</em>. When you save, sessions are auto-materialized into the Coach Planner for every matching weekday between Start and End Date. Idempotent — re-saving doesn't duplicate. Removing a row doesn't retroactively delete already-scheduled sessions. The <strong>Code</strong> field has been removed from the form (was unused).</li>
            <li><strong>Copy Day + Import Template now honour session-target rules.</strong> Sessions created via either flow no longer copy the source day's participant list. Instead they auto-populate by the same logic as <em>+ Add Session</em> — only participants whose target matches the new session's day + time (or who have no targets at all) are attached. Behaviour is now consistent across every way a Session is created.</li>
            <li><strong>Email to program owners on My Portal allocations.</strong> When a coach uses <em>+ Add Participant</em> on the My Portal Coach tab to allocate someone, every Coaching Program Owner with an email on file now receives a notification — name, contact details, who allocated them, and whether the signup-link SMS went out. Best-effort; doesn't block the SMS or the allocation if email is unconfigured.</li>
            <li><strong>Coach Planner: "Add Program" renamed to "Add Session"</strong> (session is more accurate — Program is the long-running enrolment, Session is one occurrence). The popup also no longer asks for a session-level Owner; ownership comes from the Coaching Program's multi-owner list.</li>
            <li><strong>One-time backfill of session targets from notes.</strong> If you imported a CSV before per-session targeting was deployed, your existing participants had no <code style={code}>ParticipantSessionTargets</code> rows — so a brand-new Wednesday session would auto-attach <em>everyone</em>. The API now reconstructs those targets on boot from the structured <code style={code}>[program: NAME | divisions: ...]</code> markers already stored in each participant's notes. Runs once when the table is empty. After this fix, deleting + recreating the wrongly-attached Wednesday session will only attach the right participants.</li>
            <li><strong>CSV per-session targeting now persists.</strong> Each parsed Registration Divisions division is saved as a session-target (day-of-week + start time) on the participant. When a new Program is created in the Coach Planner, only participants whose target matches the new session's day + time are auto-attached. Effects: (1) if you import a CSV that lists Mondays before any Monday Program exists, nothing visible happens at import — but when you create the Monday Program, they appear on it. (2) Creating a Tuesday Program doesn't accidentally pull in the Mon-only participant. Participants without any targets (e.g. added manually via People) still attach to every session. Editing a participant in People wipes their targets — manual edit = global.</li>
            <li><strong>CSV import: per-session targeting from Registration Divisions.</strong> When the divisions text contains a recognisable day-of-week + start time (e.g. <em>"Elite Hawks Rep Monday 6am - 7am Term 2"</em>), the participant is auto-attached only to upcoming sessions on that day at that time. A participant with two CSV rows (Mon + Fri) ends up on Mon + Fri sessions only — not Tue/Wed/Thu. When the divisions text has no parseable day/time, behaviour is unchanged: attach to every upcoming session of that program. Across multiple rows for the same person + program, payment status is OR-merged ("Registered wins"). The import results panel surfaces how many rows hit the targeted path vs. attach-all so you can spot rows whose divisions text isn't being recognised.</li>
            <li><strong>CSV import: Payment Status drives the Paid flag.</strong> The import now reads the <code style={code}>Payment Status</code> column. <strong>Registered</strong> = Paid (green badge); anything else (or blank) = Not Paid (red badge). Previously every imported row was assumed Paid.</li>
            <li><strong>Coaching session participants are now collapsible.</strong> Both the admin Coach Planner and the My Portal Coach roster show a chevron next to the <em>Participants</em> header. Sessions start <strong>collapsed</strong>; tap the header to expand and tick attendance. Select All / Deselect All only appear when expanded.</li>
            <li><strong>URLs now reflect the current page</strong> — every nav change updates the hash (e.g. <code style={code}>#coach-planner</code>, <code style={code}>#people/participants</code>). Browser back/forward, bookmarks, and shared links all work. Sub-tabs in People are part of the URL too.</li>
            <li><strong>Coach Planner: new Participants button</strong> in the toolbar (just before Lock Day) deep-links to People &rarr; Participants for fast jumping between scheduling and the participant list.</li>
            <li><strong>CSV import: smart merge.</strong> When a row matches an existing participant (by External ID or by first + last name), the import now: (a) flips the financial flag to <strong>Paid</strong> on that program (registration CSV = paid by definition), (b) only fills <em>blank</em> demographic fields (gender, email, mobile, External ID) so manually-curated values aren't clobbered.</li>
            <li><strong>Manual participant entries default to Not Paid.</strong> When you add a participant via People &rarr; Participants and tick a Coaching Program, that enrolment now starts as Not Paid (red badge) instead of Unknown (grey). Use the Financial dropdown to flip to Yes if they've already paid.</li>
            <li><strong>CSV import dedupes by first + last name when no External ID match.</strong> If the participant was already added manually (so they have no External ID), the import now matches them by name (case-insensitive) instead of creating a duplicate. The CSV's User ID is adopted onto the existing row going forward.</li>
            <li><strong>My Portal Coach: Add Participant button on every session header.</strong> Tap the green <strong>+ Add Participant</strong> button next to a session and it jumps to the Programs tab with the Coaching Program pre-selected — saves the coach scrolling around looking for the right program.</li>
            <li><strong>Participant check-in is faster + easier to tap.</strong> Coach Planner attendance ticks no longer trigger a full page refresh, so the screen doesn't scroll back to the top after every check-in. Participant chips are now larger pill-shaped tap targets — clicking <em>anywhere on the chip</em> toggles attendance (not just the small checkbox), and the chip and checkbox grow even bigger on phones (~44px tap target). Both the admin Coach Planner and the My Portal coach roster updated.</li>
            <li><strong>Mobile responsiveness — tables no longer push the page width out.</strong> Wide tables (Admin, People, Reports, Payroll, Qualifications, Availability, public Game/Court rosters) now scroll horizontally <em>within their own region</em> on phones, instead of expanding the whole page. Column padding on small screens is also tightened to fit more content before scroll kicks in. Public coach + my-roster were already responsive; this brings the rest in line.</li>
            <li><strong>Not-financial check-in modal also fires in My Portal.</strong> Previously only the admin Coach Planner asked the coach what to do when ticking a not-paid participant — coaches in My Portal were silently checking them in. Now the same 3-button dialog (Checkin and Notify / Checkin / Cancel) appears on the My Portal coach roster, with a new public token-authorised endpoint for sending the signup-reminder SMS.</li>
            <li><strong>My Portal coach roster: participant chips now colour-coded by financial status</strong> (matching the Coach Planner). Financial = light green &rarr; dark green when attended; Not financial = light red &rarr; dark red when attended; Unknown = light grey &rarr; dark grey when attended. Previously every chip turned green on attendance regardless of financial status, which hid not-paid participants from the coach.</li>
            <li><strong>Not-financial check-in modal redesigned.</strong> The browser <em>prompt</em> (which required typing "sms" or "ok") is replaced with a proper dialog containing three buttons: <strong>Checkin and Notify</strong>, <strong>Checkin</strong>, and <strong>Cancel</strong>. <em>Notify</em> is auto-disabled when the Coaching Program has no signup URL set.</li>
            <li><strong>Bug fix:</strong> a participant directly enrolled in a Coaching Program but with their financial status unset (Unknown — e.g. just imported via CSV) was inheriting a feeder program's financial value, sometimes rendering as red despite never being marked Not Paid. Direct enrolment now always wins; the feeder value is only used when the participant has no direct enrolment in this program. A new unique constraint on (participant, program-type) plus a one-time dedupe sweep prevents stale duplicate rows from causing the same symptom.</li>
            <li><strong>Coach Planner participant badges colour-coded by financial status.</strong> Financial = light green (dark green when attended); Not financial = light red (dark red when attended); Unknown = light grey (dark grey when attended). Replaces the previous solid-red default.</li>
            <li><strong>Browser tab title</strong> renamed from "Referee Roster Manager" to <strong>IBA Roster</strong>.</li>
            <li><strong>Coaching Programs can have multiple Owners.</strong> Tick one or more on the Coaching Program form. The Owner column in the Coaching Programs table now shows comma-joined names.</li>
            <li><strong>Coach decline notifications.</strong> When an assigned coach hits Reject in My Portal, every Owner of that Coaching Program with a mobile number on file receives an SMS with the coach's name, the program, and the session date/time.</li>
            <li><strong>Bug fix:</strong> the decline SMS time was off by ~10 hours due to a UTC double-conversion. Fixed — it now matches what the program shows everywhere else in the app.</li>
            <li><strong>Main navigation labels shortened:</strong> Game Planner &rarr; Game, Coach Planner &rarr; Coach, Court Planner &rarr; Court.</li>
            <li><strong>Help guide expanded</strong> with detailed accept/reject cutoff documentation. Default is 24 hours before the shift; configurable per category in Admin &rarr; Roster Response.</li>
            <li><strong>My Portal mobile experience.</strong> Tab labels shortened (My Roster &rarr; Summary; Game/Coach/Court Roster &rarr; Game/Coach/Court). Tab bar redesigned as a compact wrapping pill row instead of a tall vertical list — frees up the screen on phones.</li>
            <li><strong>Coach Planner Select All / Deselect All.</strong> Each program tile has a one-click bulk toggle in the participants header, plus an attended/total counter.</li>
            <li><strong>Linked / Feeder Coaching Programs.</strong> When Program B is a feeder of Program A, B's participants appear in A's sessions for check-in. Adding a feeder backfills attendance immediately. Useful for combined sessions across age groups or terms.</li>
            <li><strong>Participant CSV import.</strong> People &rarr; Participants gains an Import CSV button. Reads first 6 columns positionally (User ID, First Name, Last Name, Gender, Email, Mobile) plus Competition/Program and Registration Divisions by header. Auto-creates missing Coaching Programs and appends a structured note marker per program.</li>
            <li><strong>External ID + Gender</strong> on participants. External ID is the dedupe key for re-imports.</li>
            <li><strong>Auto note cleanup:</strong> when a Coaching Program is archived (manually or auto), its <code style={code}>[program: ... | divisions: ...]</code> note line is stripped from every linked participant.</li>
            <li><strong>Auto-attach allocations:</strong> allocating someone to a Coaching Program (My Portal, People page, or CSV import) auto-attaches them to all upcoming sessions for immediate check-in.</li>
            <li><strong>Schema upkeep at boot.</strong> Backend now applies idempotent schema migrations on startup so new columns/tables don't depend on the operator running SQL files.</li>
            <li><strong>Wording change:</strong> in My Portal "Register/Registered" is now "Allocate/Allocated" (a participant is allocated to a program, then completes registration via the SMS link). The signup-reminder SMS body was reworded to match.</li>
          </ul>

          <h3 style={releaseHeading}><span style={dateBadge}>10 May 2026</span>Help guide, Coaching Program lifecycle &amp; SMS</h3>
          <ul style={ul}>
            <li><strong>Comprehensive Help guide</strong> added as the rightmost top-nav item. Sidebar groups topics by Getting Started, Pages, Admin, Reference; users only see sections for features their permissions cover.</li>
            <li><strong>Coaching Program Start Date and End Date.</strong> Programs auto-archive the day after the End Date (daily cron at 12:05 AM AEDT).</li>
            <li><strong>Per-participant Financial flag</strong> (Yes / No / Unknown) on each program enrolment. Not-financial participants show a red <strong>$!</strong> badge on the Coach Planner tile; ticking attendance prompts the coach to send a signup-reminder SMS.</li>
            <li><strong>Signup URL</strong> field on Coaching Programs. Sent via SMS when a parent allocates a participant from My Portal, and used by the not-financial reminder prompt.</li>
            <li><strong>My Portal Programs tab.</strong> Public form for parents/guardians to allocate a child to a Coaching Program; signup link is SMS'd to the contact mobile.</li>
            <li><strong>Mobile-friendly My Roster:</strong> assignment cards instead of a horizontally-overflowing table.</li>
            <li><strong>Message tab:</strong> dedicated page for sending SMS — roster-per-person, custom message, bulk by role, bulk by participant. SMS one or more people in a single send.</li>
            <li><strong>SMS balance display</strong> after every send; configurable low-balance warning threshold under Admin &rarr; SMS.</li>
            <li><strong>Improved error messaging</strong> when add/update person fails — now surfaces the actual server error.</li>
          </ul>

          <h3 style={releaseHeading}><span style={dateBadge}>4-8 May 2026</span>Game assignments, sync tools &amp; sign-in</h3>
          <ul style={ul}>
            <li><strong>Game Planner supports up to 5 referees and 5 bench officials</strong> per game (normalised behind the scenes into GameAssignmentSlots).</li>
            <li><strong>Replace fixed sync windows with a date picker</strong> on Admin &rarr; Settings, plus 6-month and year-to-date quick buttons.</li>
            <li><strong>Repair Team Names</strong> tools: cross-reference based repair, endpoint-probe diagnostics, bulk-paste team-name override editor.</li>
            <li><strong>One-click "Sync teams from upstream"</strong> button — no more "Team {`{id}`}" placeholder rows.</li>
            <li><strong>12-hour times with am/pm</strong> across Game Planner, Roster View, Court Rostering, and roster emails.</li>
            <li><strong>Coaching program form shows derived end time</strong> based on start time + duration.</li>
            <li><strong>SMS integration</strong> via Mobile Message API — Admin &rarr; SMS for credentials &amp; sender ID, Send Test SMS button.</li>
            <li><strong>Sign-in switched from popup to redirect</strong> for better cross-browser/cross-domain reliability under modern Cross-Origin-Opener-Policy.</li>
            <li><strong>Availability tokens moved to URL fragment</strong> (after the <code style={code}>#</code>) so they don't leak in server logs or referrer headers; SWA security headers added.</li>
          </ul>

          <h3 style={releaseHeading}><span style={dateBadge}>March 2026</span>MFA, archive everywhere &amp; AI allocation</h3>
          <ul style={ul}>
            <li><strong>Pay visibility setting:</strong> admin can hide pay information from staff portals.</li>
            <li><strong>Archive replaces Delete</strong> across all UI. Every list has a Show Archived toggle; nothing gets hard-deleted any more.</li>
            <li><strong>Per-person availability editor</strong> built into the People modal — set weekly windows in one place.</li>
            <li><strong>AI Allocate</strong> on Game Planner: one-click bulk auto-assignment with an explainable modal.</li>
            <li><strong>TOTP-based MFA for My Portal</strong> (optional). 7-day session cookie. Admin can reset MFA for a person from People &rarr; Reset MFA.</li>
            <li><strong>Roster emails include coaching and court-official assignments</strong>, not just games.</li>
            <li><strong>Soft-archive everywhere</strong> (isArchived flag) replacing hard deletes — no more accidental data loss.</li>
            <li><strong>Security hardening:</strong> JWT auth on every endpoint, CORS restriction, parameterised IN clauses, rate limiting, dropped spoofable identity headers.</li>
          </ul>

          <div style={tipBox}>
            Spot something missing or wrong in the notes? Tell the admin — release notes live at the
            top of <code style={code}>Help.jsx</code> in the SECTIONS array, so they're easy to
            patch in a deploy.
          </div>
        </div>
      );
    }
  }
];

// Merge in the guides from HelpGuides.jsx: replaced sections, extra
// content appended to existing ones, and new sections after their anchor.
const ALL_SECTIONS = (() => {
  const list = SECTIONS.map(s => {
    let render = REPLACE_SECTIONS[s.key] || s.render;
    if (EXTRA_CONTENT[s.key]) {
      const base = render;
      const extra = EXTRA_CONTENT[s.key];
      render = () => <>{base()}{extra()}</>;
    }
    return { ...s, render };
  });
  for (const ns of NEW_SECTIONS) {
    const idx = list.findIndex(s => s.key === ns.after);
    list.splice(idx >= 0 ? idx + 1 : list.length, 0, ns);
  }
  return list;
})();

// ---------- helper: does the user have the requirement? ----------
function isVisible(canView, requires) {
  if (requires == null) return true;
  if (Array.isArray(requires)) {
    return requires.some(r => canView(r.pageKey, r.sectionKey));
  }
  return canView(requires.pageKey, requires.sectionKey);
}

// ---------- main component ----------
export default function Help() {
  const { canView } = usePermissions();

  const visibleSections = useMemo(
    () => ALL_SECTIONS.filter(s => isVisible(canView, s.requires)),
    [canView]
  );

  // Active section: whichever is in the URL hash, falling back to the first visible.
  // Hash format: #help-<key>
  const initialFromHash = (() => {
    if (typeof window === 'undefined') return null;
    const m = (window.location.hash || '').match(/^#help-(.+)$/);
    return m ? m[1] : null;
  })();
  const [activeKey, setActiveKey] = useState(
    initialFromHash && visibleSections.find(s => s.key === initialFromHash)
      ? initialFromHash
      : (visibleSections[0]?.key || 'welcome')
  );

  function selectSection(key) {
    setActiveKey(key);
    try {
      window.history.replaceState({}, '', `#help-${key}`);
    } catch { /* ignore */ }
    // Scroll content area to top on section change.
    if (typeof document !== 'undefined') {
      const main = document.getElementById('help-content');
      if (main) main.scrollTop = 0;
    }
  }

  const groups = useMemo(() => {
    const out = [];
    const seen = new Set();
    visibleSections.forEach(s => {
      if (!seen.has(s.group)) { out.push({ name: s.group, items: [] }); seen.add(s.group); }
      out[out.findIndex(g => g.name === s.group)].items.push(s);
    });
    return out;
  }, [visibleSections]);

  const active = visibleSections.find(s => s.key === activeKey) || visibleSections[0];

  return (
    <div className="help-page" style={{ display: 'flex', minHeight: 'calc(100vh - 200px)', background: '#fafafa' }}>
      {/* Sidebar */}
      <aside
        className="help-sidebar"
        style={{
          width: 260,
          flex: '0 0 260px',
          background: '#fff',
          borderRight: '1px solid #e0e0e0',
          padding: '1rem 0.5rem',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 100px)',
          position: 'sticky',
          top: 0
        }}
      >
        {groups.map(group => (
          <div key={group.name} style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#888', padding: '0 0.75rem 0.4rem', fontWeight: 700 }}>
              {group.name}
            </div>
            {group.items.map(s => (
              <button
                key={s.key}
                onClick={() => selectSection(s.key)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.55rem 0.75rem',
                  background: activeKey === s.key ? '#fef0f3' : 'transparent',
                  color: activeKey === s.key ? '#A4161A' : '#333',
                  fontWeight: activeKey === s.key ? 700 : 500,
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        ))}
      </aside>

      {/* Mobile fallback: a select dropdown above the content for narrow screens */}
      <div className="help-mobile-nav" style={{ display: 'none' }}>
        <select
          value={activeKey}
          onChange={e => selectSection(e.target.value)}
          style={{ width: '100%', padding: '0.6rem', fontSize: '0.95rem', border: '1px solid #ccc', borderRadius: 6 }}
        >
          {groups.map(group => (
            <optgroup key={group.name} label={group.name}>
              {group.items.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Main content */}
      <main id="help-content" style={{ flex: 1, overflowY: 'auto', maxHeight: 'calc(100vh - 100px)' }}>
        {active ? active.render() : (
          <div style={sectionWrap}>
            <h2 style={h2}>No help available</h2>
            <p style={para}>You don't currently have access to any features that have help topics.</p>
          </div>
        )}
      </main>

      {/* Inline media-query: stack on narrow screens, swap sidebar for select. */}
      <style>{`
        @media (max-width: 720px) {
          .help-page { flex-direction: column !important; }
          .help-sidebar { display: none !important; }
          .help-mobile-nav { display: block !important; padding: 0.75rem !important; background: #fff; border-bottom: 1px solid #e0e0e0; }
        }
      `}</style>
    </div>
  );
}
